import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut } from 'lucide-react';
import './CourierDashboard.css';

import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import TrackingMap from '@/components/TrackingMap';
import { getRouteDirections, geocodeAddress } from '@/services/routes';
import {
  acceptOrder,
  confirmCashReturn,
  getMe,
  getPendingCashReturns,
  getVerification,
  listAvailableOrders,
  listMyOrders,
  rejectOrder,
  setOnline,
  uploadVerificationDocument,
  uploadVerificationFace,
  updateLocation,
  updateMe,
  updateMyOrderStatus,
} from '@/services/courier';

const ROUTE_REFRESH_MS = Number(process.env.REACT_APP_ROUTE_REFRESH_MS) || 30000;
const GEOCODE_REFRESH_MS = Number(process.env.REACT_APP_GEOCODE_REFRESH_MS) || 15000;
const COURIER_ORDERS_REFRESH_MS = Number(process.env.REACT_APP_COURIER_ORDERS_REFRESH_MS) || 12000;

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMoney(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return 'R$ 0,00';
  const reais = Number.isInteger(num) ? num / 100 : num;
  return reais.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatReaisNumber(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '0,00';
  return parsed.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function resolveCourierRideNet(order) {
  const explicitNet = Number(order?.courier_net_earning_cents);
  if (Number.isFinite(explicitNet)) return Math.max(0, explicitNet);
  const deliveryFee = Number(order?.delivery_fee || 0);
  if (!Number.isFinite(deliveryFee)) return 0;
  const explicitAdminFee = Number(order?.admin_fee_cents);
  if (Number.isFinite(explicitAdminFee)) {
    return Math.max(0, Math.trunc(deliveryFee) - Math.max(0, Math.trunc(explicitAdminFee)));
  }
  const explicitAdminPercent = Number(order?.admin_fee_percent);
  if (Number.isFinite(explicitAdminPercent) && explicitAdminPercent > 0) {
    const percentFee = Math.round(Math.trunc(deliveryFee) * (Math.min(100, Math.max(0, explicitAdminPercent)) / 100));
    return Math.max(0, Math.trunc(deliveryFee) - percentFee);
  }
  return Math.max(0, Math.trunc(deliveryFee));
}

function normalizeBirthDateValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const brMatch = raw.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
  if (brMatch) {
    const [, day, month, year] = brMatch;
    return `${year}-${month}-${day}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return '';
}

function normalizeAvailablePayload(payload) {
  if (Array.isArray(payload)) {
    return { items: payload, meta: {} };
  }

  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    meta: payload?.meta && typeof payload.meta === 'object' ? payload.meta : {},
  };
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function formatAddress(address) {
  if (!address) return '-';
  if (typeof address === 'string') return address;
  const parts = [
    address.address,
    address.full_address,
    address.street || address.rua,
    address.number || address.numero,
    address.neighborhood || address.bairro,
    address.city || address.cidade,
    address.state || address.uf,
    address.zipcode || address.cep,
    address.complement || address.complemento,
  ].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return address.label || '-';
}

function resolveOrderRestaurantCoords(order) {
  const restaurant = order?.restaurant || {};
  const address = order?.delivery_address || {};
  const lat = toNumber(restaurant.lat ?? address.restaurant_lat);
  const lng = toNumber(restaurant.lng ?? address.restaurant_lng);
  if (lat == null || lng == null) return null;
  return { lat, lng };
}

function resolveOrderCustomerCoords(order) {
  const address = order?.delivery_address || {};
  const lat = toNumber(address.lat ?? address.latitude);
  const lng = toNumber(address.lng ?? address.longitude);
  if (lat == null || lng == null) return null;
  return { lat, lng };
}

function resolveStoreCustomerDistanceKm(order) {
  const explicitDistance = Number(order?.delivery_distance_km);
  if (Number.isFinite(explicitDistance) && explicitDistance >= 0) return explicitDistance;

  const restaurantCoords = resolveOrderRestaurantCoords(order);
  const customerCoords = resolveOrderCustomerCoords(order);
  if (!restaurantCoords || !customerCoords) return null;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(customerCoords.lat - restaurantCoords.lat);
  const dLng = toRad(customerCoords.lng - restaurantCoords.lng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(restaurantCoords.lat)) * Math.cos(toRad(customerCoords.lat)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Number((6371 * c).toFixed(3));
}

function formatDistanceKm(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '-';
  return `${parsed.toFixed(1).replace('.', ',')} km`;
}

function normalizeServiceKind(value) {
  const token = String(value || '').trim().toLowerCase();
  if (token === 'viajem') return 'viagem';
  if (token === 'viagem' || token === 'envio') return token;
  return '';
}

function resolveServiceRequest(order) {
  const direct = order?.service_request && typeof order.service_request === 'object' ? order.service_request : null;
  if (direct) return direct;

  const fromAddress = order?.delivery_address && typeof order.delivery_address === 'object'
    ? order.delivery_address
    : {};
  const kind = normalizeServiceKind(fromAddress.service_kind);
  const type = String(fromAddress.service_type || '').trim().toLowerCase();
  if (!kind || type !== 'envios_viagens') return null;

  return {
    type,
    kind,
    kind_label: kind === 'envio' ? 'Envio' : 'Viagem',
    item_hint: fromAddress.service_item_hint || null,
  };
}

function getServiceBadgeLabel(order) {
  const service = resolveServiceRequest(order);
  if (!service) return '';
  return normalizeServiceKind(service.kind) === 'envio' ? 'ENVIO' : 'VIAGEM';
}

function getServiceGearHint(order) {
  const service = resolveServiceRequest(order);
  if (!service) return '';
  const kind = normalizeServiceKind(service.kind);
  if (kind === 'envio') return 'Levar bag';
  return 'Levar capacete extra';
}

function getServiceCardClass(order) {
  const service = resolveServiceRequest(order);
  if (!service) return '';
  const kind = normalizeServiceKind(service.kind);
  if (kind === 'envio') return 'courier-service-card courier-service-card--envio';
  return 'courier-service-card courier-service-card--viagem';
}

function formatClockMmSs(totalSeconds) {
  const safeSeconds = Math.max(0, Math.trunc(Number(totalSeconds) || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatCashReturnStatusLabel(status) {
  const key = String(status || '').trim().toLowerCase();
  if (key === 'overdue') return 'Vencida';
  if (key === 'code_generated') return 'Codigo gerado';
  if (key === 'settled') return 'Devolvido';
  if (key === 'cancelled') return 'Cancelada';
  return 'Pendente';
}


function resolveDeliveryKind(address) {
  const raw = String(address?.type || address?.label || '').toLowerCase();
  if (raw.includes('work') || raw.includes('trabalho') || raw.includes('empresa') || raw.includes('office') || raw.includes('comercial')) {
    return 'delivery_work';
  }
  return 'delivery_home';
}

function getRouteColor(index, total) {
  if (!total || total <= 1) return '#FF6A00';
  const startHue = 24;
  const endHue = 280;
  const ratio = Math.max(0, Math.min(1, index / (total - 1)));
  const hue = startHue + (endHue - startHue) * ratio;
  return `hsl(${hue}, 80%, 52%)`;
}

const DEFAULT_VERIFICATION = {
  status: 'not_submitted',
  note: null,
  submitted_at: null,
  reviewed_at: null,
  reviewed_by: null,
  face_image_url: null,
  document_image_url: null,
  has_face_image: false,
  has_document_image: false,
};

function getVerificationPresentation(status) {
  const normalized = String(status || 'not_submitted').toLowerCase();
  if (normalized === 'approved') {
    return {
      label: 'Aprovado',
      color: '#166534',
      background: '#dcfce7',
      message: 'Cadastro validado pelo admin. Voce pode ficar online normalmente.',
    };
  }
  if (normalized === 'pending') {
    return {
      label: 'Em analise',
      color: '#92400e',
      background: '#fef3c7',
      message: 'Cadastro em analise. Aguarde aprovacao do admin para ficar online.',
    };
  }
  if (normalized === 'rejected') {
    return {
      label: 'Reprovado',
      color: '#991b1b',
      background: '#fee2e2',
      message: 'Cadastro reprovado. Reenvie selfie e documento para nova analise.',
    };
  }
  return {
    label: 'Nao enviado',
    color: '#1f2937',
    background: '#e5e7eb',
    message: 'Envie selfie e documento com foto para concluir seu cadastro.',
  };
}

function getMissingCourierValidationFields(form) {
  const missing = [];
  if (!String(form?.motorcycle_brand || '').trim()) missing.push('marca da moto');
  if (!String(form?.motorcycle_model || '').trim()) missing.push('modelo da moto');
  const emergencyDigits = String(form?.emergency_contact_phone || '').replace(/\D/g, '');
  if (emergencyDigits.length < 8) missing.push('contato de emergencia');
  const birthDate = normalizeBirthDateValue(form?.birth_date);
  if (!birthDate) missing.push('data de nascimento');
  return missing;
}

function hasConfiguredPayoutData(data) {
  const source = data && typeof data === 'object' ? data : {};
  const method = String(source.payout_method || source.method || 'pix').trim().toLowerCase();
  const pixKey = String(source.pix_key || '').trim();
  const bankFields = [
    source.bank_name,
    source.bank_agency,
    source.bank_account,
    source.bank_account_type,
    source.bank_holder_name,
    source.bank_holder_document,
  ].some((value) => String(value || '').trim().length > 0);

  if (method === 'bank') return bankFields;
  if (method === 'pix') return pixKey.length > 0;
  return pixKey.length > 0 || bankFields;
}


export default function CourierDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, signOut } = useAuth();

  const [me, setMe] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    cpf: '',
    placa: '',
    motorcycle_brand: '',
    motorcycle_model: '',
    emergency_contact_phone: '',
    birth_date: '',
    payout_method: 'pix',
    pix_key: '',
    bank_name: '',
    bank_agency: '',
    bank_account: '',
    bank_account_type: '',
    bank_holder_name: '',
    bank_holder_document: '',
  });
  const [verification, setVerification] = useState(DEFAULT_VERIFICATION);
  const [uploadingFace, setUploadingFace] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  const [availableOrders, setAvailableOrders] = useState([]);
  const [availableMeta, setAvailableMeta] = useState({});
  const [myOrders, setMyOrders] = useState([]);

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPayout, setSavingPayout] = useState(false);
  const [isPayoutEditing, setIsPayoutEditing] = useState(true);
  const [savingPayoutSchedule, setSavingPayoutSchedule] = useState(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [cashReturnCode, setCashReturnCode] = useState('');
  const [cashReturnCodeError, setCashReturnCodeError] = useState('');
  const [selectedCashReturnId, setSelectedCashReturnId] = useState('');
  const [confirmingCashReturnId, setConfirmingCashReturnId] = useState(null);
  const [updatingOnline, setUpdatingOnline] = useState(false);
  const [updatingLocation, setUpdatingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [routeData, setRouteData] = useState(null);
  const [multiRoutes, setMultiRoutes] = useState([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [previewRoutes, setPreviewRoutes] = useState([]);
  const [previewRouteLoading, setPreviewRouteLoading] = useState(false);
  const [previewRouteError, setPreviewRouteError] = useState('');
  const [resolvedCustomerCoords, setResolvedCustomerCoords] = useState(null);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');
  const [loadingLists, setLoadingLists] = useState(true);
  const [acceptingId, setAcceptingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [selectedAvailableOrderId, setSelectedAvailableOrderId] = useState(null);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  const [view, setView] = useState('profile');
  const [deliveryCode, setDeliveryCode] = useState('');
  const [showCodePanel, setShowCodePanel] = useState(false);
  const [showMobileInfoPanel, setShowMobileInfoPanel] = useState(false);
  const [submittingCode, setSubmittingCode] = useState(false);
  const [cancelledOrderNotice, setCancelledOrderNotice] = useState(null);
  const [clockNowMs, setClockNowMs] = useState(() => Date.now());
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  ));
  const [mobilePanelSections, setMobilePanelSections] = useState({
    profile: true,
    availability: false,
    payout: false,
  });

  const silentLocationUpdating = useRef(false);
  const locationPrompted = useRef(false);
  const routeRequestRef = useRef({ key: '', ts: 0 });
  const multiRouteCacheRef = useRef(new Map());
  const geocodeRequestRef = useRef({ key: '', ts: 0 });
  const refreshListsPromiseRef = useRef(null);
  const knownCancelledOrderIdsRef = useRef(new Set());
  const didPrimeCancelledOrdersRef = useRef(false);
  const initializedUserIdRef = useRef(null);
  const authRedirectingRef = useRef(false);
  const codeInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const profileSectionRef = useRef(null);
  const availabilitySectionRef = useRef(null);
  const payoutSectionRef = useRef(null);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraTargetType, setCameraTargetType] = useState(null);
  const [cameraError, setCameraError] = useState('');

  const resolveGeolocationError = useCallback((error) => {
    if (!error) return 'Nao foi possivel obter sua localizacao.';
    if (error.code === 1) return 'Permissao de localizacao negada pelo navegador.';
    if (error.code === 2) return 'Localizacao indisponivel no momento.';
    if (error.code === 3) return 'Tempo esgotado ao buscar sua localizacao.';
    return error.message || 'Nao foi possivel obter sua localizacao.';
  }, []);

  const handleApiError = useCallback(
    async (error, fallbackMessage) => {
      const statusCode = error?.statusCode || error?.status;

      if (statusCode === 401) {
        if (authRedirectingRef.current) return;
        authRedirectingRef.current = true;
        toast({
          variant: 'destructive',
          title: 'Sessao invalida',
          description: 'Sessao invalida, faca login novamente',
        });
        await signOut();
        navigate('/login', { replace: true });
        return;
      }



      const backendMessage =
        error?.originalError?.detail ||
        error?.getUserFriendlyMessage?.() ||
        error?.message ||
        fallbackMessage;

      toast({
        variant: 'destructive',
        title: 'Erro',
        description: backendMessage,
      });
    },
    [navigate, signOut, toast]
  );

  const handleLogout = useCallback(async () => {
    await signOut();
    navigate('/login', { replace: true });
  }, [navigate, signOut]);

  const isRecentLocation = useCallback((value, maxAgeMs = 300000) => {
    if (!value) return false;
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return false;
    return Date.now() - dt.getTime() <= maxAgeMs;
  }, []);

  const updateLocationFromBrowser = useCallback(
    (silent = false) => {
      if (!me?.is_online) {
        const message = 'Fique online para atualizar sua localizacao.';
        setLocationError(message);
        if (!silent) {
          toast({
            variant: 'destructive',
            title: 'Disponibilidade',
            description: message,
          });
        }
        return;
      }

      if (!window.isSecureContext) {
        const message = 'Localizacao requer HTTPS ou localhost.';
        setLocationError(message);
        if (!silent) {
          toast({
            variant: 'destructive',
            title: 'Permissao de localizacao',
            description: message,
          });
        }
        return;
      }

      if (!navigator.geolocation) {
        const message = 'Geolocalizacao nao suportada neste dispositivo.';
        setLocationError(message);
        if (!silent) {
          toast({
            variant: 'destructive',
            title: 'Erro',
            description: message,
          });
        }
        return;
      }

      if (silentLocationUpdating.current) {
        return;
      }

      silentLocationUpdating.current = true;
      if (!silent) {
        setUpdatingLocation(true);
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          (async () => {
            try {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              const response = await updateLocation(lat, lng);
              const nextLat = Number.isFinite(response?.lat) ? response.lat : lat;
              const nextLng = Number.isFinite(response?.lng) ? response.lng : lng;
              const nextLocationAt = response?.last_location_at || new Date().toISOString();

              setMe((prev) => ({
                ...(prev || {}),
                lat: nextLat,
                lng: nextLng,
                last_location_at: nextLocationAt,
              }));

              setLocationError('');
              if (!silent) {
                toast({ title: 'Sucesso', description: 'Localizacao atualizada.' });
              }
            } catch (error) {
              const message =
                error?.originalError?.detail ||
                error?.getUserFriendlyMessage?.() ||
                error?.message ||
                'Nao foi possivel atualizar localizacao';
              setLocationError(message);
              if (!silent) {
                await handleApiError(error, 'Nao foi possivel atualizar localizacao');
              }
            } finally {
              silentLocationUpdating.current = false;
              if (!silent) {
                setUpdatingLocation(false);
              }
            }
          })();
        },
        (geoError) => {
          const message = resolveGeolocationError(geoError);
          setLocationError(message);
          silentLocationUpdating.current = false;
          if (!silent) {
            setUpdatingLocation(false);
            toast({
              variant: 'destructive',
              title: 'Erro',
              description: message,
            });
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    },
    [handleApiError, me?.is_online, resolveGeolocationError, toast]
  );

  const loadProfile = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const data = await getMe();
      let verificationData = data?.verification || null;
      if (!verificationData) {
        try {
          verificationData = await getVerification();
        } catch {
          verificationData = DEFAULT_VERIFICATION;
        }
      }
      setMe({ ...(data || {}), verification: verificationData });
      setVerification(verificationData || DEFAULT_VERIFICATION);
      setProfileForm({
        name: data?.name || '',
        phone: data?.phone || '',
        cpf: data?.cpf || '',
        placa: data?.placa || '',
        motorcycle_brand: data?.motorcycle_brand || '',
        motorcycle_model: data?.motorcycle_model || '',
        emergency_contact_phone: data?.emergency_contact_phone || '',
        birth_date: normalizeBirthDateValue(data?.birth_date),
        payout_method: data?.payout_method || data?.payout_account?.method || 'pix',
        pix_key: data?.pix_key || data?.payout_account?.pix_key || '',
        bank_name: data?.bank_name || data?.payout_account?.bank_name || '',
        bank_agency: data?.bank_agency || data?.payout_account?.bank_agency || '',
        bank_account: data?.bank_account || data?.payout_account?.bank_account || '',
        bank_account_type: data?.bank_account_type || data?.payout_account?.bank_account_type || '',
        bank_holder_name: data?.bank_holder_name || data?.payout_account?.bank_holder_name || '',
        bank_holder_document: data?.bank_holder_document || data?.payout_account?.bank_holder_document || '',
      });
      setIsPayoutEditing(
        !hasConfiguredPayoutData({
          payout_method: data?.payout_method || data?.payout_account?.method || 'pix',
          pix_key: data?.pix_key || data?.payout_account?.pix_key || '',
          bank_name: data?.bank_name || data?.payout_account?.bank_name || '',
          bank_agency: data?.bank_agency || data?.payout_account?.bank_agency || '',
          bank_account: data?.bank_account || data?.payout_account?.bank_account || '',
          bank_account_type: data?.bank_account_type || data?.payout_account?.bank_account_type || '',
          bank_holder_name: data?.bank_holder_name || data?.payout_account?.bank_holder_name || '',
          bank_holder_document: data?.bank_holder_document || data?.payout_account?.bank_holder_document || '',
        })
      );
    } catch (error) {
      await handleApiError(error, 'Nao foi possivel carregar seu cadastro');
    } finally {
      setLoadingProfile(false);
    }
  }, [handleApiError]);

  const loadAvailableOrders = useCallback(async () => {
    const verificationStatus = String(me?.verification?.status || verification?.status || '').trim().toLowerCase();
    const profileSnapshot = {
      motorcycle_brand: profileForm.motorcycle_brand || me?.motorcycle_brand || '',
      motorcycle_model: profileForm.motorcycle_model || me?.motorcycle_model || '',
      emergency_contact_phone: profileForm.emergency_contact_phone || me?.emergency_contact_phone || '',
      birth_date: profileForm.birth_date || me?.birth_date || '',
    };
    const missingFields = getMissingCourierValidationFields(profileSnapshot);
    const canOperate = verificationStatus === 'approved' && missingFields.length === 0;

    if (!me?.is_online || !canOperate) {
      setAvailableOrders([]);
      setAvailableMeta({
        available_status: 'ready',
        assigned_status: 'awaiting_pickup',
        distance_filter_applied: false,
        reason: !me?.is_online ? 'courier_offline' : 'courier_not_eligible',
      });
      return;
    }

    if (me?.is_online && !isRecentLocation(me?.last_location_at)) {
      updateLocationFromBrowser(true);
      return;
    }
    const response = await listAvailableOrders();
    const normalized = normalizeAvailablePayload(response);
    setAvailableOrders(normalized.items);
    setAvailableMeta(normalized.meta);
  }, [
    isRecentLocation,
    me?.is_online,
    me?.last_location_at,
    me?.verification?.status,
    me?.motorcycle_brand,
    me?.motorcycle_model,
    me?.emergency_contact_phone,
    me?.birth_date,
    profileForm.motorcycle_brand,
    profileForm.motorcycle_model,
    profileForm.emergency_contact_phone,
    profileForm.birth_date,
    updateLocationFromBrowser,
    verification?.status,
  ]);

  const loadMyOrders = useCallback(async () => {
    const response = await listMyOrders();
    setMyOrders(Array.isArray(response) ? response : []);
  }, []);

  const loadCashReturns = useCallback(async () => {
    const response = await getPendingCashReturns();
    const payload = response?.cash_returns && typeof response.cash_returns === 'object'
      ? response.cash_returns
      : null;

    if (!payload) return;

    setMe((prev) => ({
      ...(prev || {}),
      cash_returns: payload,
      courier_status: response?.courier_status || prev?.courier_status || 'active',
      courier_status_reason: response?.courier_status_reason ?? prev?.courier_status_reason ?? null,
    }));
  }, []);

  const refreshLists = useCallback(async () => {
    if (refreshListsPromiseRef.current) {
      return refreshListsPromiseRef.current;
    }

    const pending = (async () => {
      setLoadingLists(true);
      try {
        await Promise.all([loadAvailableOrders(), loadMyOrders(), loadCashReturns()]);
      } catch (error) {
        await handleApiError(error, 'Nao foi possivel carregar os pedidos');
      } finally {
        setLoadingLists(false);
        refreshListsPromiseRef.current = null;
      }
    })();

    refreshListsPromiseRef.current = pending;
    return pending;
  }, [handleApiError, loadAvailableOrders, loadCashReturns, loadMyOrders]);

  useEffect(() => {
    if (!user?.id) {
      initializedUserIdRef.current = null;
      authRedirectingRef.current = false;
      knownCancelledOrderIdsRef.current = new Set();
      didPrimeCancelledOrdersRef.current = false;
      setCancelledOrderNotice(null);
      return;
    }

    if (initializedUserIdRef.current === user.id) {
      return;
    }

    initializedUserIdRef.current = user.id;

    const init = async () => {
      await Promise.all([loadProfile(), refreshLists()]);
    };

    init();
  }, [user?.id, loadProfile, refreshLists]);

  useEffect(() => {
    if (!me?.is_online) {
      return;
    }

    const shouldPrompt = !locationPrompted.current && (me?.lat == null || me?.lng == null);
    const shouldRefreshNow = !isRecentLocation(me?.last_location_at, 120000);

    if (shouldPrompt) {
      updateLocationFromBrowser(false);
      locationPrompted.current = true;
    } else if (shouldRefreshNow) {
      updateLocationFromBrowser(true);
    }

    const intervalId = setInterval(() => {
      updateLocationFromBrowser(true);
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [me?.is_online, me?.lat, me?.lng, me?.last_location_at, isRecentLocation, updateLocationFromBrowser]);

  useEffect(() => {
    if (!me?.is_online) {
      return undefined;
    }

    let cancelled = false;
    const runSync = async () => {
      if (cancelled) return;
      await refreshLists();
    };

    runSync();
    const intervalId = window.setInterval(() => {
      runSync();
    }, COURIER_ORDERS_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [me?.is_online, refreshLists]);



  useEffect(() => {
    if (!Array.isArray(availableOrders) || availableOrders.length == 0) {
      setSelectedAvailableOrderId(null);
      return;
    }
    if (selectedAvailableOrderId && availableOrders.some((order) => order.id === selectedAvailableOrderId)) {
      return;
    }
    setSelectedAvailableOrderId(availableOrders[0].id);
  }, [availableOrders, selectedAvailableOrderId]);

  useEffect(() => {
    if (!showCodePanel) return;
    const timer = setTimeout(() => {
      if (codeInputRef.current) {
        codeInputRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [showCodePanel]);

  useEffect(() => {
    if (showCodePanel) {
      setShowMobileInfoPanel(false);
    }
  }, [showCodePanel]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => {
      setIsMobileViewport(event.matches);
    };
    setIsMobileViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setMobilePanelSections({
        profile: true,
        availability: true,
        payout: true,
      });
    }
  }, [isMobileViewport]);

  const toggleMobilePanelSection = useCallback((section) => {
    if (!isMobileViewport) return;
    setMobilePanelSections((prev) => ({
      ...prev,
      [section]: !prev?.[section],
    }));
  }, [isMobileViewport]);

  const scrollToMobilePanelSection = useCallback((section, sectionRef) => {
    if (isMobileViewport) {
      setMobilePanelSections((prev) => ({
        ...prev,
        [section]: true,
      }));
    }
    const target = sectionRef?.current;
    if (!target || typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [isMobileViewport]);

  const canShowPickupWarning = useMemo(
    () => availableMeta?.reason === 'orders_without_pickup_coordinates',
    [availableMeta]
  );

  const nextAvailableOrder = useMemo(() => {
    if (selectedAvailableOrderId && Array.isArray(availableOrders)) {
      const selected = availableOrders.find((order) => order.id === selectedAvailableOrderId);
      if (selected) return selected;
    }
    return availableOrders[0] || null;
  }, [availableOrders, selectedAvailableOrderId]);

  const acceptedOrders = useMemo(() => {
    if (!Array.isArray(myOrders)) return [];
    return myOrders.filter((order) => {
      const accepted = Boolean(order?.courier_accepted_at || order?.metadata?.courier_accepted_at);
      const statusAccepted = ['awaiting_pickup', 'out_for_delivery', 'delivered'].includes(order?.status);
      return accepted || statusAccepted;
    });
  }, [myOrders]);

  const activeOrders = useMemo(
    () => acceptedOrders.filter((order) => !['delivered', 'cancelled'].includes(order.status)),
    [acceptedOrders]
  );
  const canAcceptMoreOrders = activeOrders.length < 7;

  const previewOrder = useMemo(() => nextAvailableOrder, [nextAvailableOrder]);
  const previewRestaurantCoords = useMemo(() => resolveOrderRestaurantCoords(previewOrder), [previewOrder]);
  const previewCustomerCoords = useMemo(() => resolveOrderCustomerCoords(previewOrder), [previewOrder]);



  const pickupOrders = useMemo(
    () => activeOrders.filter((order) => ['awaiting_pickup', 'ready'].includes(order.status)),
    [activeOrders]
  );

  const deliveryOrders = useMemo(
    () => activeOrders.filter((order) => order.status === 'out_for_delivery'),
    [activeOrders]
  );

  const [focusedOrderId, setFocusedOrderId] = useState(null);

  const activeOrder = useMemo(() => {
    if (!activeOrders.length) return null;
    if (focusedOrderId) {
      const found = activeOrders.find((order) => order.id === focusedOrderId);
      if (found) return found;
    }
    return pickupOrders[0] || deliveryOrders[0] || activeOrders[0];
  }, [activeOrders, deliveryOrders, focusedOrderId, pickupOrders]);

  useEffect(() => {
    if (showCodePanel && !activeOrder) {
      setShowCodePanel(false);
      setDeliveryCode('');
    }
  }, [showCodePanel, activeOrder]);

  useEffect(() => {
    if (loadingLists) return;
    const cancelledOrders = Array.isArray(myOrders)
      ? myOrders.filter((order) => String(order?.status || '').toLowerCase() === 'cancelled')
      : [];

    if (!didPrimeCancelledOrdersRef.current) {
      cancelledOrders.forEach((order) => {
        const id = String(order?.id || '').trim();
        if (id) knownCancelledOrderIdsRef.current.add(id);
      });
      didPrimeCancelledOrdersRef.current = true;
      return;
    }

    let lastCancelled = null;
    cancelledOrders.forEach((order) => {
      const id = String(order?.id || '').trim();
      if (!id || knownCancelledOrderIdsRef.current.has(id)) return;
      knownCancelledOrderIdsRef.current.add(id);
      if (order?.courier_id || order?.courier_accepted_at) {
        lastCancelled = order;
      }
    });

    if (!lastCancelled) return;

    const orderLabel = String(lastCancelled.order_number || lastCancelled.id || '').trim() || 'sem numero';
    setCancelledOrderNotice({
      id: String(lastCancelled.id),
      orderLabel,
      occurredAt: Date.now(),
    });
    setShowCodePanel(false);
    setDeliveryCode('');
    toast({
      variant: 'destructive',
      title: 'Pedido cancelado',
      description: `O pedido ${orderLabel} foi cancelado. Aguarde uma nova corrida.`,
    });
  }, [loadingLists, myOrders, toast]);

  useEffect(() => {
    if (!cancelledOrderNotice?.id) return undefined;
    const timer = window.setTimeout(() => {
      setCancelledOrderNotice(null);
    }, 20000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [cancelledOrderNotice?.id, cancelledOrderNotice?.occurredAt]);

  const isAwaitingPickup = useMemo(() => {
    if (!activeOrder) return false;
    return ['awaiting_pickup', 'ready'].includes(activeOrder.status);
  }, [activeOrder]);

  const activeOrderPhaseClock = useMemo(() => {
    const timing = activeOrder?.courier_timing;
    if (!timing || typeof timing !== 'object') return null;

    const deadlineAt = String(timing.deadline_at || '').trim();
    if (!deadlineAt) return null;
    const deadlineMs = new Date(deadlineAt).getTime();
    if (!Number.isFinite(deadlineMs)) return null;

    const timingPhase = String(timing.phase || '').toLowerCase() === 'delivery' ? 'delivery' : 'pickup';
    const orderStatus = String(activeOrder?.status || '').trim().toLowerCase();
    const phase = orderStatus === 'out_for_delivery'
      ? 'delivery'
      : (['awaiting_pickup', 'ready'].includes(orderStatus) ? 'pickup' : timingPhase);
    const shouldTrustTimingLabels = phase === timingPhase;
    const instanceLabel = String(
      (shouldTrustTimingLabels ? timing.instance_label : null)
      || (phase === 'delivery' ? 'Entrega do pedido ao cliente' : 'Retirada do pedido na loja')
    );
    const clockLabel = String(
      (shouldTrustTimingLabels ? timing.clock_label : null)
      || (phase === 'delivery' ? 'Entregar em' : 'Retirar em')
    );
    const rawRemaining = Math.floor((deadlineMs - clockNowMs) / 1000);
    return {
      phase,
      instanceLabel,
      clockLabel,
      countdown: formatClockMmSs(rawRemaining),
      isOverdue: rawRemaining < 0,
    };
  }, [activeOrder?.courier_timing, activeOrder?.status, clockNowMs]);
  const activeOrderTimingDeadline = activeOrder?.courier_timing?.deadline_at;

  useEffect(() => {
    if (view !== 'map' || !activeOrderTimingDeadline) {
      return undefined;
    }
    setClockNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setClockNowMs(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [view, activeOrderTimingDeadline]);

  const rawCustomerCoords = useMemo(() => {
    const address = activeOrder?.delivery_address || {};
    const lat = toNumber(address.lat ?? address.latitude);
    const lng = toNumber(address.lng ?? address.longitude);
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [activeOrder]);

  useEffect(() => {
    if (!activeOrder) {
      setResolvedCustomerCoords(null);
      setGeocodeError('');
      return;
    }

    if (rawCustomerCoords) {
      setResolvedCustomerCoords(rawCustomerCoords);
      setGeocodeError('');
      return;
    }

    const address = activeOrder?.delivery_address || {};
    const fulfillmentType = (activeOrder?.fulfillment_type || 'delivery').toLowerCase();
    if (fulfillmentType === 'pickup' || address.type === 'pickup') {
      setResolvedCustomerCoords(null);
      setGeocodeError('');
      return;
    }

    const textParts = [
      address.label,
      address.address,
      address.full_address,
      address.street,
      address.number,
      address.neighborhood,
      address.city,
      address.state,
      address.zipcode,
    ]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);
    const text = textParts.join(', ');

    if (!text) {
      setResolvedCustomerCoords(null);
      setGeocodeError('');
      return;
    }

    const key = text.toLowerCase();
    const now = Date.now();
    if (geocodeRequestRef.current.key === key && (now - geocodeRequestRef.current.ts) < GEOCODE_REFRESH_MS) {
      return;
    }

    geocodeRequestRef.current = { key, ts: now };
    setGeocodeLoading(true);
    setGeocodeError('');

    geocodeAddress({ text })
      .then((data) => {
        const lat = Number(data?.lat);
        const lng = Number(data?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setResolvedCustomerCoords({ lat, lng });
        } else {
          setResolvedCustomerCoords(null);
          setGeocodeError('Nao foi possivel localizar o endereco');
        }
      })
      .catch((err) => {
        setResolvedCustomerCoords(null);
        setGeocodeError(err?.message || 'Nao foi possivel localizar o endereco');
      })
      .finally(() => {
        setGeocodeLoading(false);
      });
  }, [activeOrder, rawCustomerCoords]);

  const customerCoords = resolvedCustomerCoords;

  const restaurantCoords = useMemo(() => {
    const restaurant = activeOrder?.restaurant || {};
    const address = activeOrder?.delivery_address || {};
    const lat = toNumber(restaurant.lat ?? address.restaurant_lat);
    const lng = toNumber(restaurant.lng ?? address.restaurant_lng);
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [activeOrder]);


  const courierCoords = useMemo(() => {
    const lat = toNumber(me?.lat);
    const lng = toNumber(me?.lng);
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [me?.lat, me?.lng]);

  const destinationCoords = isAwaitingPickup ? restaurantCoords : customerCoords;


  const routeTargets = useMemo(() => {
    return activeOrders
      .map((order) => {
        const isPickup = ['awaiting_pickup', 'ready'].includes(order.status);
        const dest = isPickup
          ? resolveOrderRestaurantCoords(order)
          : order.status === 'out_for_delivery'
          ? resolveOrderCustomerCoords(order)
          : null;
        if (!dest) return null;
        return { order, dest, type: isPickup ? 'pickup' : 'delivery' };
      })
      .filter(Boolean);
  }, [activeOrders]);

  const extraMarkers = useMemo(() => {
    const total = routeTargets.length;
    return routeTargets.map((item, index) => ({
      id: item.order.id,
      lat: item.dest.lat,
      lng: item.dest.lng,
      kind: item.type === 'pickup'
        ? 'pickup'
        : resolveDeliveryKind(item.order?.delivery_address),
      label: item.type === 'pickup'
        ? (item.order?.restaurant?.name || 'Loja')
        : (item.order?.customer?.name || item.order?.customer_name || item.order?.delivery_address?.name || 'Cliente'),
      color: getRouteColor(index, total),
    }));
  }, [routeTargets]);

  const previewMarkers = useMemo(() => {
    if (!previewOrder) return [];
    const markers = [];
    if (previewRestaurantCoords) {
      markers.push({
        id: `preview-restaurant-${previewOrder.id}`,
        lat: previewRestaurantCoords.lat,
        lng: previewRestaurantCoords.lng,
        kind: 'pickup',
        label: previewOrder?.restaurant?.name || 'Loja',
        color: '#fb923c',
      });
    }
    if (previewCustomerCoords) {
      markers.push({
        id: `preview-customer-${previewOrder.id}`,
        lat: previewCustomerCoords.lat,
        lng: previewCustomerCoords.lng,
        kind: resolveDeliveryKind(previewOrder?.delivery_address),
        label: previewOrder?.customer?.name || previewOrder?.customer_name || previewOrder?.delivery_address?.name || 'Cliente',
        color: '#60a5fa',
      });
    }
    return markers;
  }, [previewOrder, previewRestaurantCoords, previewCustomerCoords]);


  useEffect(() => {
    let cancelled = false;

    const loadPreviewRoutes = async () => {
      if (!courierCoords || !previewRestaurantCoords) {
        setPreviewRoutes([]);
        setPreviewRouteError('');
        return;
      }

      setPreviewRouteLoading(true);
      setPreviewRouteError('');

      try {
        const features = [];
        const first = await getRouteDirections({ start: courierCoords, end: previewRestaurantCoords });
        if (first?.geometry) {
          features.push({ type: 'Feature', geometry: first.geometry, properties: { type: 'preview_pickup', color: '#fb923c' } });
        }
        if (previewCustomerCoords) {
          const second = await getRouteDirections({ start: previewRestaurantCoords, end: previewCustomerCoords });
          if (second?.geometry) {
            features.push({ type: 'Feature', geometry: second.geometry, properties: { type: 'preview_delivery', color: '#60a5fa' } });
          }
        }
        if (!cancelled) {
          setPreviewRoutes(features);
        }
      } catch (err) {
        if (!cancelled) {
          setPreviewRoutes([]);
          setPreviewRouteError(err?.message || 'Nao foi possivel carregar a rota');
        }
      } finally {
        if (!cancelled) {
          setPreviewRouteLoading(false);
        }
      }
    };

    loadPreviewRoutes();
    return () => {
      cancelled = true;
    };
  }, [courierCoords?.lat, courierCoords?.lng, previewOrder?.id, previewRestaurantCoords?.lat, previewRestaurantCoords?.lng, previewCustomerCoords?.lat, previewCustomerCoords?.lng]);

  useEffect(() => {
    if (!activeOrder || !courierCoords || !destinationCoords) {
      setRouteData(null);
      setRouteError('');
      return;
    }

    const key = `${courierCoords.lat},${courierCoords.lng}->${destinationCoords.lat},${destinationCoords.lng}`;
    const now = Date.now();
    if (routeRequestRef.current.key === key && (now - routeRequestRef.current.ts) < ROUTE_REFRESH_MS) {
      return;
    }

    routeRequestRef.current = { key, ts: now };
    setRouteLoading(true);
    setRouteError('');

    getRouteDirections({ start: courierCoords, end: destinationCoords })
      .then((data) => {
        setRouteData(data || null);
      })
      .catch((err) => {
        setRouteData(null);
        setRouteError(err?.message || 'Nao foi possivel carregar a rota');
      })
      .finally(() => {
        setRouteLoading(false);
      });
  }, [activeOrder?.id, courierCoords?.lat, courierCoords?.lng, destinationCoords?.lat, destinationCoords?.lng, isAwaitingPickup]);


  useEffect(() => {
    let cancelled = false;

    const loadMultiRoutes = async () => {
      if (!courierCoords || routeTargets.length === 0) {
        setMultiRoutes([]);
        return;
      }

      const now = Date.now();
      const results = [];
            const maxRoutes = 7;
      const visibleTargets = routeTargets.slice(0, maxRoutes);
      const total = visibleTargets.length;
      for (const [idx, item] of visibleTargets.entries()) {
        const key = `${item.order.id}:${courierCoords.lat},${courierCoords.lng}->${item.dest.lat},${item.dest.lng}:${item.type}`;
        const cached = multiRouteCacheRef.current.get(key);
        if (cached && (now - cached.ts) < ROUTE_REFRESH_MS) {
          results.push(cached.feature);
          continue;
        }

        try {
          const data = await getRouteDirections({ start: courierCoords, end: item.dest });
          if (data?.geometry) {
            const color = getRouteColor(idx, total);
            const feature = { type: 'Feature', geometry: data.geometry, properties: { orderId: item.order.id, type: item.type, color } };
            multiRouteCacheRef.current.set(key, { ts: Date.now(), feature });
            results.push(feature);
          }
        } catch {
          // ignore errors per route
        }
      }

      if (!cancelled) {
        setMultiRoutes(results);
      }
    };

    loadMultiRoutes();
    return () => {
      cancelled = true;
    };
  }, [courierCoords?.lat, courierCoords?.lng, routeTargets]);

  const stopCameraStream = useCallback(() => {
    const stream = cameraStreamRef.current;
    if (!stream) return;
    try {
      stream.getTracks().forEach((track) => track.stop());
    } catch {
      // ignore
    }
    cameraStreamRef.current = null;
  }, []);

  const closeCameraDialog = useCallback(() => {
    stopCameraStream();
    setCameraDialogOpen(false);
    setCameraTargetType(null);
    setCameraError('');
  }, [stopCameraStream]);

  const openCameraDialog = useCallback((targetType) => {
    if (!window.isSecureContext) {
      toast({
        variant: 'destructive',
        title: 'Camera indisponivel',
        description: 'A camera exige HTTPS ou localhost.',
      });
      return;
    }

    if (!navigator?.mediaDevices?.getUserMedia) {
      toast({
        variant: 'destructive',
        title: 'Camera indisponivel',
        description: 'Seu navegador nao suporta captura direta de camera.',
      });
      return;
    }

    setCameraTargetType(targetType);
    setCameraError('');
    setCameraDialogOpen(true);
  }, [toast]);

  useEffect(() => {
    if (!cameraDialogOpen || !cameraTargetType) return;
    let cancelled = false;

    const startCamera = async () => {
      stopCameraStream();
      setCameraError('');

      const facingMode = cameraTargetType === 'face' ? 'user' : 'environment';

      try {
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: facingMode },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
            audio: false,
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
        }

        if (cancelled) {
          try {
            stream.getTracks().forEach((track) => track.stop());
          } catch {
            // ignore
          }
          return;
        }

        cameraStreamRef.current = stream;
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
          await cameraVideoRef.current.play().catch(() => {});
        }
      } catch (error) {
        const message = error?.message || 'Nao foi possivel acessar a camera.';
        setCameraError(message);
      }
    };

    startCamera();
    return () => {
      cancelled = true;
      stopCameraStream();
    };
  }, [cameraDialogOpen, cameraTargetType, stopCameraStream]);

  useEffect(() => () => {
    stopCameraStream();
  }, [stopCameraStream]);

  async function handleSaveProfile(event) {
    event.preventDefault();
    setSavingProfile(true);

    try {
      const payload = {
        phone: profileForm.phone?.trim() || null,
        placa: profileForm.placa?.trim() || null,
        motorcycle_brand: profileForm.motorcycle_brand?.trim() || null,
        motorcycle_model: profileForm.motorcycle_model?.trim() || null,
        emergency_contact_phone: profileForm.emergency_contact_phone?.trim() || null,
        birth_date: normalizeBirthDateValue(profileForm.birth_date) || null,
      };

      const updated = await updateMe(payload);
      setMe(updated);
      setVerification(updated?.verification || verification);
      setProfileForm({
        name: updated?.name || '',
        phone: updated?.phone || '',
        cpf: updated?.cpf || '',
        placa: updated?.placa || '',
        motorcycle_brand: updated?.motorcycle_brand || '',
        motorcycle_model: updated?.motorcycle_model || '',
        emergency_contact_phone: updated?.emergency_contact_phone || '',
        birth_date: normalizeBirthDateValue(updated?.birth_date),
        payout_method: updated?.payout_method || updated?.payout_account?.method || 'pix',
        pix_key: updated?.pix_key || updated?.payout_account?.pix_key || '',
        bank_name: updated?.bank_name || updated?.payout_account?.bank_name || '',
        bank_agency: updated?.bank_agency || updated?.payout_account?.bank_agency || '',
        bank_account: updated?.bank_account || updated?.payout_account?.bank_account || '',
        bank_account_type: updated?.bank_account_type || updated?.payout_account?.bank_account_type || '',
        bank_holder_name: updated?.bank_holder_name || updated?.payout_account?.bank_holder_name || '',
        bank_holder_document: updated?.bank_holder_document || updated?.payout_account?.bank_holder_document || '',
      });

      toast({ title: 'Sucesso', description: 'Cadastro atualizado com sucesso.' });
    } catch (error) {
      await handleApiError(error, 'Nao foi possivel atualizar seu cadastro');
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleSavePayout(event) {
    event.preventDefault();
    if (!isPayoutEditing && hasSavedPayoutData) {
      toast({
        title: 'Dados salvos',
        description: 'Clique em "Trocar dados de recebimento" para editar.',
      });
      return;
    }
    setSavingPayout(true);

    try {
      const payload = {
        payout_method: profileForm.payout_method || 'pix',
        pix_key: profileForm.pix_key?.trim() || null,
        bank_name: profileForm.bank_name?.trim() || null,
        bank_agency: profileForm.bank_agency?.trim() || null,
        bank_account: profileForm.bank_account?.trim() || null,
        bank_account_type: profileForm.bank_account_type?.trim() || null,
        bank_holder_name: profileForm.bank_holder_name?.trim() || null,
        bank_holder_document: profileForm.bank_holder_document?.trim() || null,
      };

      const updated = await updateMe(payload);
      setMe(updated);
      setVerification(updated?.verification || verification);
      setProfileForm((prev) => ({
        ...prev,
        payout_method: updated?.payout_method || updated?.payout_account?.method || 'pix',
        pix_key: updated?.pix_key || updated?.payout_account?.pix_key || '',
        bank_name: updated?.bank_name || updated?.payout_account?.bank_name || '',
        bank_agency: updated?.bank_agency || updated?.payout_account?.bank_agency || '',
        bank_account: updated?.bank_account || updated?.payout_account?.bank_account || '',
        bank_account_type: updated?.bank_account_type || updated?.payout_account?.bank_account_type || '',
        bank_holder_name: updated?.bank_holder_name || updated?.payout_account?.bank_holder_name || '',
        bank_holder_document: updated?.bank_holder_document || updated?.payout_account?.bank_holder_document || '',
      }));
      setIsPayoutEditing(false);

      toast({ title: 'Sucesso', description: 'Dados de recebimento atualizados.' });
    } catch (error) {
      await handleApiError(error, 'Nao foi possivel salvar os dados de recebimento');
    } finally {
      setSavingPayout(false);
    }
  }

  async function handleSelectPayoutSchedule(nextSchedule) {
    if (!['weekly', 'monthly'].includes(nextSchedule)) return;
    setSavingPayoutSchedule(true);

    try {
      const updated = await updateMe({ payout_schedule_preference: nextSchedule });
      setMe((prev) => {
        const prevPayload = prev && typeof prev === 'object' ? prev : {};
        const nextPayload = updated && typeof updated === 'object' ? updated : {};
        const mergedFinancial = {
          ...(prevPayload.financial && typeof prevPayload.financial === 'object' ? prevPayload.financial : {}),
          ...(nextPayload.financial && typeof nextPayload.financial === 'object' ? nextPayload.financial : {}),
        };

        const currentScheduleFromServer = String(mergedFinancial.payout_schedule || '').toLowerCase();
        const normalizedCurrentSchedule = currentScheduleFromServer === 'monthly' ? 'monthly' : 'weekly';
        const requestedScheduleFromServer = String(mergedFinancial.payout_schedule_requested || '').toLowerCase();
        const normalizedRequestedSchedule = ['weekly', 'monthly'].includes(requestedScheduleFromServer)
          ? requestedScheduleFromServer
          : (nextSchedule === normalizedCurrentSchedule ? null : nextSchedule);

        mergedFinancial.payout_schedule = normalizedCurrentSchedule;
        mergedFinancial.payout_schedule_requested = normalizedRequestedSchedule;
        mergedFinancial.payout_schedule_change_pending = Boolean(
          normalizedRequestedSchedule && normalizedRequestedSchedule !== normalizedCurrentSchedule
        );

        return {
          ...prevPayload,
          ...nextPayload,
          financial: mergedFinancial,
        };
      });
      setVerification(updated?.verification || verification);
      setProfileForm((prev) => ({
        ...prev,
        payout_method: updated?.payout_method || updated?.payout_account?.method || 'pix',
        pix_key: updated?.pix_key || updated?.payout_account?.pix_key || '',
        bank_name: updated?.bank_name || updated?.payout_account?.bank_name || '',
        bank_agency: updated?.bank_agency || updated?.payout_account?.bank_agency || '',
        bank_account: updated?.bank_account || updated?.payout_account?.bank_account || '',
        bank_account_type: updated?.bank_account_type || updated?.payout_account?.bank_account_type || '',
        bank_holder_name: updated?.bank_holder_name || updated?.payout_account?.bank_holder_name || '',
        bank_holder_document: updated?.bank_holder_document || updated?.payout_account?.bank_holder_document || '',
      }));

      const pending = Boolean(updated?.financial?.payout_schedule_change_pending);
      const currentScheduleAfterSave = String(updated?.financial?.payout_schedule || payoutSchedule).toLowerCase() === 'monthly'
        ? 'monthly'
        : 'weekly';
      if (pending) {
        const waitDescription = currentScheduleAfterSave === 'monthly'
          ? 'A solicitacao ficara em espera ate o pagamento mensal (5o dia util, ate 09:00).'
          : 'A solicitacao ficara em espera ate o pagamento semanal (segunda-feira, ate 09:00).';
        toast({
          title: 'Solicitacao salva',
          description: `Troca solicitada para ${nextSchedule === 'monthly' ? 'mensal' : 'semanal'}. ${waitDescription}`,
        });
      } else {
        toast({
          title: 'Periodicidade atualizada',
          description: `Recebimento alterado para ${nextSchedule === 'monthly' ? 'mensal' : 'semanal'}.`,
        });
      }

      await loadProfile();
    } catch (error) {
      await handleApiError(error, 'Nao foi possivel alterar a periodicidade de recebimento');
    } finally {
      setSavingPayoutSchedule(false);
    }
  }

  async function sendVerificationFile(type, file) {
    if (!file) return false;

    if (!String(file.type || '').startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Arquivo invalido',
        description: 'Envie apenas arquivos de imagem.',
      });
      return false;
    }

    const isFace = type === 'face';
    if (isFace) {
      setUploadingFace(true);
    } else {
      setUploadingDocument(true);
    }

    try {
      const response = isFace
        ? await uploadVerificationFace(file)
        : await uploadVerificationDocument(file);
      setVerification(response || DEFAULT_VERIFICATION);
      setMe((prev) => ({ ...(prev || {}), verification: response || DEFAULT_VERIFICATION }));

      toast({
        title: isFace ? 'Selfie enviada' : 'Documento enviado',
        description: 'Arquivo enviado com sucesso.',
      });

      if (response?.status === 'pending') {
        toast({
          title: 'Cadastro enviado para analise',
          description: 'O admin precisa aprovar antes de voce ficar online.',
        });
      }
      return true;
    } catch (error) {
      await handleApiError(error, 'Nao foi possivel enviar o arquivo');
      return false;
    } finally {
      if (isFace) {
        setUploadingFace(false);
      } else {
        setUploadingDocument(false);
      }
    }
  }

  async function handleCaptureFromCamera() {
    if (!cameraTargetType || !cameraVideoRef.current) return;

    const video = cameraVideoRef.current;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    if (!width || !height) {
      setCameraError('A camera ainda nao esta pronta. Tente novamente.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setCameraError('Nao foi possivel processar a foto.');
      return;
    }

    ctx.drawImage(video, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) {
      setCameraError('Falha ao capturar imagem da camera.');
      return;
    }

    const file = new File([blob], `${cameraTargetType}-${Date.now()}.jpg`, { type: 'image/jpeg' });
    const success = await sendVerificationFile(cameraTargetType, file);
    if (success) {
      closeCameraDialog();
    }
  }

  async function handleSetOnline(nextOnline, redirectToMap = false) {
    setUpdatingOnline(true);

    try {
      const response = await setOnline(nextOnline);
      setMe((prev) => ({
        ...(prev || {}),
        is_online: Boolean(response?.is_online),
        last_available_at: response?.last_available_at || prev?.last_available_at,
        financial: response?.financial || prev?.financial,
      }));

      toast({
        title: 'Disponibilidade atualizada',
        description: nextOnline ? 'Voce esta online.' : 'Voce esta offline.',
      });

      if (nextOnline) {
        updateLocationFromBrowser(false);
        locationPrompted.current = true;
      }

      if (redirectToMap) {
        setView('map');
      }
    } catch (error) {
      await handleApiError(error, 'Nao foi possivel atualizar disponibilidade');
    } finally {
      setUpdatingOnline(false);
    }
  }

  async function handleGoOnline() {
    if (String(me?.courier_status_reason || '').toLowerCase() === 'cash_return_overdue') {
      setView('profile');
      toast({
        variant: 'destructive',
        title: 'Conta bloqueada para novas corridas',
        description: 'Existe devolucao em dinheiro vencida. Regularize no bloco "Devolucao de dinheiro para loja".',
      });
      return;
    }

    const missingFields = getMissingCourierValidationFields(profileForm);
    if (missingFields.length > 0) {
      if (me?.is_online) {
        await handleSetOnline(false);
      }
      setView('profile');
      toast({
        variant: 'destructive',
        title: 'Dados obrigatorios faltando',
        description: `Faltam dados obrigatorios para validacao: ${missingFields.join(', ')}.`,
      });
      return;
    }

    if (me?.is_online) {
      await refreshLists();
      setView('map');
      return;
    }
    await handleSetOnline(true, true);
    await refreshLists();
  }


  function handleBackToProfile() {
    setShowCodePanel(false);
    setDeliveryCode('');
    setView('profile');
  }

  async function handleGoOffline() {
    await handleSetOnline(false);
    handleBackToProfile();
  }

  function handleUpdateLocation() {
    updateLocationFromBrowser(false);
  }

  async function handleAcceptOrder(orderId) {
    setAcceptingId(orderId);
    try {
      await acceptOrder(orderId);
      toast({ title: 'Pedido aceito', description: 'Pedido atribuido a voce com sucesso.' });
      await refreshLists();
    } catch (error) {
      await handleApiError(error, 'Nao foi possivel aceitar o pedido');
    } finally {
      setAcceptingId(null);
    }
  }

  async function handleRejectOrder(orderId) {
    setRejectingId(orderId);
    try {
      await rejectOrder(orderId);
      toast({ title: 'Corrida rejeitada', description: 'A corrida foi removida da sua lista disponivel.' });
      await refreshLists();
    } catch (error) {
      await handleApiError(error, 'Nao foi possivel rejeitar a corrida');
    } finally {
      setRejectingId(null);
    }
  }

  function handleToggleCodePanel() {
    if (!activeOrder) {
      toast({
        variant: 'destructive',
        title: 'Sem pedido ativo',
        description: 'Aceite uma corrida antes de inserir o codigo.',
      });
      return;
    }

    setShowCodePanel((prev) => !prev);
  }

  async function handleConfirmDeliveryCode() {
    if (!activeOrder) {
      toast({
        variant: 'destructive',
        title: 'Sem pedido ativo',
        description: 'Aceite uma corrida antes de confirmar a entrega.',
      });
      return;
    }

    if (!canConfirmDelivery) {
      toast({
        variant: 'destructive',
        title: 'Pedido ainda nao saiu para entrega',
        description: 'A confirmacao so e permitida quando o pedido estiver em rota.',
      });
      return;
    }

    const code = deliveryCode.trim();
    if (!code) {
      toast({
        variant: 'destructive',
        title: 'Codigo obrigatorio',
        description: 'Informe o codigo do cliente para confirmar a entrega.',
      });
      return;
    }

    setSubmittingCode(true);
    setUpdatingOrderId(activeOrder.id);

    try {
      await updateMyOrderStatus(activeOrder.id, 'delivered', code);
      toast({ title: 'Entrega confirmada', description: 'Pedido finalizado com sucesso.' });
      setDeliveryCode('');
      setShowCodePanel(false);
      await Promise.all([refreshLists(), loadProfile()]);
    } catch (error) {
      await handleApiError(error, 'Nao foi possivel confirmar a entrega');
    } finally {
      setSubmittingCode(false);
      setUpdatingOrderId(null);
    }
  }

  async function handleConfirmCashReturnCode() {
    const candidateItems = Array.isArray(cashReturnItems) ? cashReturnItems : [];
    if (!candidateItems.length) {
      toast({
        variant: 'destructive',
        title: 'Sem pendencia selecionada',
        description: 'Selecione uma pendencia de devolucao para confirmar.',
      });
      return;
    }

    const code = String(cashReturnCode || '').replace(/\D/g, '').slice(0, 8);
    if (!code) {
      setCashReturnCodeError('Codigo obrigatorio.');
      toast({
        variant: 'destructive',
        title: 'Codigo obrigatorio',
        description: 'Digite o codigo informado pela loja para confirmar a devolucao.',
      });
      return;
    }

    setCashReturnCodeError('');
    const selectedId = String(selectedCashReturnItem?.id || selectedCashReturnId || '').trim();
    const prioritizedItems = [
      ...candidateItems.filter((item) => String(item?.id || '') === selectedId),
      ...candidateItems.filter((item) => String(item?.id || '') !== selectedId),
    ];

    const isExpectedCodeMismatchError = (error) => {
      const statusCode = Number(error?.statusCode || error?.status || 0);
      const message = String(
        error?.originalError?.detail
        || error?.getUserFriendlyMessage?.()
        || error?.message
        || ''
      ).toLowerCase();
      if (statusCode === 404) return true;
      if (statusCode !== 400) return false;
      return (
        message.includes('codigo de devolucao invalido')
        || message.includes('codigo invalido')
        || message.includes('ainda nao gerou codigo')
      );
    };

    const fallbackConfirmingId = selectedId || String(prioritizedItems[0]?.id || '');
    setConfirmingCashReturnId(fallbackConfirmingId);
    try {
      let response = null;
      let matchedItem = null;
      let unexpectedError = null;

      for (const item of prioritizedItems) {
        const currentId = String(item?.id || '').trim();
        if (!currentId) continue;
        try {
          response = await confirmCashReturn(currentId, code);
          matchedItem = item;
          break;
        } catch (error) {
          if (isExpectedCodeMismatchError(error)) {
            continue;
          }
          unexpectedError = error;
          break;
        }
      }

      if (!response || !matchedItem) {
        if (unexpectedError) throw unexpectedError;
        const invalidMessage = 'Codigo invalido. Confirme com o lojista e tente novamente.';
        setCashReturnCodeError(invalidMessage);
        toast({
          variant: 'destructive',
          title: 'Codigo invalido',
          description: invalidMessage,
        });
        return;
      }

      const payload = response?.cash_returns && typeof response.cash_returns === 'object'
        ? response.cash_returns
        : null;
      setMe((prev) => ({
        ...(prev || {}),
        cash_returns: payload || prev?.cash_returns || {},
        courier_status: response?.courier_status || prev?.courier_status || 'active',
        courier_status_reason: response?.courier_status_reason ?? prev?.courier_status_reason ?? null,
      }));
      setCashReturnCode('');
      setCashReturnCodeError('');
      setSelectedCashReturnId(String(matchedItem?.id || selectedId || ''));
      const matchedStoreName = matchedItem?.restaurant?.name || matchedItem?.restaurant_name || 'Loja';
      toast({
        title: 'Devolucao confirmada',
        description: `Valor em dinheiro regularizado com sucesso para ${matchedStoreName}.`,
      });
      await refreshLists();
    } catch (error) {
      const statusCode = Number(error?.statusCode || error?.status || 0);
      const message = String(
        error?.originalError?.detail
        || error?.getUserFriendlyMessage?.()
        || error?.message
        || ''
      ).toLowerCase();
      if (statusCode === 400 && (message.includes('codigo de devolucao invalido') || message.includes('codigo invalido'))) {
        const invalidMessage = 'Codigo invalido. Confirme com o lojista e tente novamente.';
        setCashReturnCodeError(invalidMessage);
        toast({
          variant: 'destructive',
          title: 'Codigo invalido',
          description: invalidMessage,
        });
        return;
      }
      await handleApiError(error, 'Nao foi possivel confirmar a devolucao de dinheiro');
    } finally {
      setConfirmingCashReturnId(null);
    }
  }

  if (!user) return null;

  const canConfirmDelivery = activeOrder?.status === 'out_for_delivery';
  const showInProgressCard = deliveryOrders.length > 0;
  const showPickupCard = pickupOrders.length > 0;
  const hasActiveOrder = Boolean(activeOrder);
  const effectiveVerification = me?.verification || verification || DEFAULT_VERIFICATION;
  const verificationPresentation = getVerificationPresentation(effectiveVerification?.status);
  const hasFaceVerification = Boolean(effectiveVerification?.has_face_image);
  const hasDocumentVerification = Boolean(effectiveVerification?.has_document_image);
  const verificationStatus = String(effectiveVerification?.status || '').trim().toLowerCase();
  const hasVerificationSubmission = Boolean(effectiveVerification?.submitted_at || effectiveVerification?.reviewed_at);
  const hasRegisteredVerificationImages = Boolean(
    (hasFaceVerification && hasDocumentVerification) ||
    hasVerificationSubmission ||
    verificationStatus === 'pending' ||
    verificationStatus === 'approved'
  );
  const backendMissingValidationDataFields = Array.isArray(me?.validation_requirements?.missing_fields)
    ? me.validation_requirements.missing_fields.filter((field) => typeof field === 'string' && field.trim().length > 0)
    : [];
  const localMissingValidationDataFields = getMissingCourierValidationFields(profileForm);
  const missingValidationDataFields = backendMissingValidationDataFields.length > 0
    ? backendMissingValidationDataFields
    : localMissingValidationDataFields;
  const hasAllRequiredValidationData = missingValidationDataFields.length === 0;
  const canRequestOnline = Boolean(effectiveVerification?.status === 'approved' && hasAllRequiredValidationData);
  const isVerificationRejected = verificationStatus === 'rejected';
  const mustCompleteVerificationStep = !loadingProfile && !hasRegisteredVerificationImages;
  const isVerificationPending = verificationStatus === 'pending';
  const availabilityLabel = me?.is_online ? 'Ir para o mapa' : 'Ficar disponivel';
  const isProfileSectionOpen = !isMobileViewport || mobilePanelSections.profile;
  const isAvailabilitySectionOpen = !isMobileViewport || mobilePanelSections.availability;
  const isPayoutSectionOpen = !isMobileViewport || mobilePanelSections.payout;
  const emailValue = me?.email || user?.email || '';
  const financial = me?.financial || {};
  const pendingSessionCents = Number(financial?.pending_session_cents || 0);
  const receivableCents = Number(financial?.receivable_cents || 0);
  const pendingTotalCents = Number(financial?.pending_total_cents || (pendingSessionCents + receivableCents));
  const waitingNextCycleCents = Number(financial?.waiting_next_cycle_cents || 0);
  const readyForPayoutCents = Number(financial?.ready_for_payout_cents || receivableCents);
  const currentCycleUnpaidCents = Number(financial?.current_cycle_unpaid_cents || 0);
  const lifetimeEarnedCents = Number(financial?.lifetime_earned_cents || pendingTotalCents);
  const deliveriesCompletedTotal = Number(financial?.deliveries_completed_total || me?.stats?.delivered_orders || 0);
  const payoutSchedule = String(financial?.payout_schedule || 'weekly').toLowerCase() === 'monthly' ? 'monthly' : 'weekly';
  const payoutScheduleRequested = String(financial?.payout_schedule_requested || '').toLowerCase();
  const payoutSchedulePending = Boolean(financial?.payout_schedule_change_pending) || (
    (payoutScheduleRequested === 'weekly' || payoutScheduleRequested === 'monthly') &&
    payoutScheduleRequested !== payoutSchedule
  );
  const pendingTargetSchedule = payoutSchedulePending && (
    payoutScheduleRequested === 'weekly' || payoutScheduleRequested === 'monthly'
  )
    ? payoutScheduleRequested
    : null;
  const pendingScheduleLabel = pendingTargetSchedule === 'monthly' ? 'mensal' : 'semanal';
  const payoutScheduleHint = payoutSchedulePending
    ? (payoutSchedule === 'weekly' && payoutScheduleRequested === 'monthly'
      ? 'Solicitacao pendente para mensal. A mudanca ativa apos o pagamento semanal de segunda-feira (ate 09:00).'
      : payoutSchedule === 'monthly' && payoutScheduleRequested === 'weekly'
        ? 'Solicitacao pendente para semanal. A mudanca ativa apos o pagamento mensal do 5o dia util (ate 09:00).'
        : `Solicitacao pendente para ${payoutScheduleRequested === 'monthly' ? 'mensal' : 'semanal'}.`)
    : `Recebimento atual: ${payoutSchedule === 'monthly' ? 'mensal' : 'semanal'}.`;
  const courierAdminFeeGlobalPercent = Number(financial?.admin_fee_global_percent ?? 0);
  const hasCourierAdminFeeGlobalPercent = Number.isFinite(courierAdminFeeGlobalPercent) && courierAdminFeeGlobalPercent > 0;
  const courierAdminFeeRules = (Array.isArray(financial?.admin_fee_rules) ? financial.admin_fee_rules : [])
    .map((rule) => ({
      max_delivery_fee_reais: Number(rule?.max_delivery_fee_reais),
      percent: Number(rule?.percent),
      is_active: rule?.is_active !== false
    }))
    .filter((rule) => Number.isFinite(rule.max_delivery_fee_reais) && Number.isFinite(rule.percent) && rule.is_active)
    .sort((a, b) => a.max_delivery_fee_reais - b.max_delivery_fee_reais);
  const courierAdminFeeRuleSummary = courierAdminFeeRules.length > 0
    ? courierAdminFeeRules.map((rule) => `ate R$ ${formatReaisNumber(rule.max_delivery_fee_reais)}: ${rule.percent.toFixed(2)}%`).join(' | ')
    : '';
  const courierAdminFeeInfoText = courierAdminFeeRules.length > 0
    ? `Regras por faixa do frete: ${courierAdminFeeRuleSummary}.${hasCourierAdminFeeGlobalPercent ? ` Fallback global: ${courierAdminFeeGlobalPercent.toFixed(2)}%.` : ''}`
    : hasCourierAdminFeeGlobalPercent
      ? `Regra global ativa: ${courierAdminFeeGlobalPercent.toFixed(2)}% sobre o frete.`
      : 'Regra legada ativa: desconto fixo por entrega.';
  const nextPayoutAt = financial?.next_payout_at ? formatDate(financial.next_payout_at) : '-';
  const payoutMethod = profileForm.payout_method || me?.payout_method || me?.payout_account?.method || 'pix';
  const hasSavedPayoutData = hasConfiguredPayoutData({
    payout_method: me?.payout_method || me?.payout_account?.method || payoutMethod,
    pix_key: me?.pix_key || me?.payout_account?.pix_key || '',
    bank_name: me?.bank_name || me?.payout_account?.bank_name || '',
    bank_agency: me?.bank_agency || me?.payout_account?.bank_agency || '',
    bank_account: me?.bank_account || me?.payout_account?.bank_account || '',
    bank_account_type: me?.bank_account_type || me?.payout_account?.bank_account_type || '',
    bank_holder_name: me?.bank_holder_name || me?.payout_account?.bank_holder_name || '',
    bank_holder_document: me?.bank_holder_document || me?.payout_account?.bank_holder_document || '',
  });
  const payoutFieldsLocked = hasSavedPayoutData && !isPayoutEditing;
  const cashReturnsPayload = (me?.cash_returns && typeof me.cash_returns === 'object') ? me.cash_returns : {};
  const cashReturnSummary = cashReturnsPayload?.summary && typeof cashReturnsPayload.summary === 'object'
    ? cashReturnsPayload.summary
    : {};
  const cashReturnStores = Array.isArray(cashReturnsPayload?.stores) ? cashReturnsPayload.stores : [];
  const cashReturnItems = Array.isArray(cashReturnsPayload?.items) ? cashReturnsPayload.items : [];
  const cashReturnPendingCount = Number(cashReturnSummary?.pending_count || 0);
  const cashReturnPendingTotalCents = Number(cashReturnSummary?.pending_total_cents || 0);
  const cashReturnOverdueCount = Number(cashReturnSummary?.overdue_count || 0);
  const cashReturnBlocked = Boolean(
    cashReturnsPayload?.is_blocked_for_cash_return
    || cashReturnOverdueCount > 0
    || String(me?.courier_status_reason || '').toLowerCase() === 'cash_return_overdue'
  );
  const selectedCashReturnItem = useMemo(() => {
    if (!cashReturnItems.length) return null;
    if (selectedCashReturnId) {
      const found = cashReturnItems.find((item) => String(item?.id || '') === String(selectedCashReturnId));
      if (found) return found;
    }
    return cashReturnItems[0] || null;
  }, [cashReturnItems, selectedCashReturnId]);
  const hasPendingCashReturns = cashReturnPendingCount > 0;

  useEffect(() => {
    if (!cashReturnItems.length) {
      if (selectedCashReturnId) setSelectedCashReturnId('');
      if (cashReturnCode) setCashReturnCode('');
      if (cashReturnCodeError) setCashReturnCodeError('');
      return;
    }
    if (!selectedCashReturnId || !cashReturnItems.some((item) => String(item?.id || '') === String(selectedCashReturnId))) {
      setSelectedCashReturnId(String(cashReturnItems[0]?.id || ''));
      setCashReturnCode('');
      if (cashReturnCodeError) setCashReturnCodeError('');
    }
  }, [cashReturnCode, cashReturnCodeError, cashReturnItems, selectedCashReturnId]);

  const secondsUntilRollover = Number(financial?.seconds_until_rollover);
  const hasRolloverCountdown = Number.isFinite(secondsUntilRollover) && secondsUntilRollover > 0;
  const rolloverCountdownLabel = hasRolloverCountdown
    ? `${Math.floor(secondsUntilRollover / 3600)}h ${Math.floor((secondsUntilRollover % 3600) / 60)}m`
    : null;
  const combinedMarkers = [...extraMarkers, ...previewMarkers];
  const combinedRoutes = [...multiRoutes, ...previewRoutes];
  const hasMobileInfoContent = Boolean(
    locationError
    || canShowPickupWarning
    || showPickupCard
    || showInProgressCard
    || availableOrders.length > 0
    || hasPendingCashReturns
  );
  const displayRestaurantCoords = activeOrder ? restaurantCoords : previewRestaurantCoords;
  const displayCustomerCoords = activeOrder ? customerCoords : previewCustomerCoords;
  const displayRestaurantLabel = activeOrder?.restaurant?.name || previewOrder?.restaurant?.name;
  const displayCustomerLabel = activeOrder?.customer?.name || activeOrder?.customer_name || previewOrder?.customer?.name || previewOrder?.customer_name || previewOrder?.delivery_address?.name;
  const displayCustomerKind = activeOrder ? resolveDeliveryKind(activeOrder?.delivery_address) : resolveDeliveryKind(previewOrder?.delivery_address);
  const currentHour = new Date().getHours();
  const useNightMapStyle = currentHour >= 18 || currentHour < 6;
  const courierMapStyleUrl = useNightMapStyle
    ? (process.env.REACT_APP_MAP_STYLE_URL_DARK || 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json')
    : (process.env.REACT_APP_MAP_STYLE_URL || 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json');

  if (mustCompleteVerificationStep) {
    return (
      <div className="courier-page-shell">
        <div className="courier-panel-page">
          <button className="btn-link courier-panel-back" onClick={handleLogout}>
            <LogOut size={16} />
            Sair
          </button>

          <section className="card courier-panel-section">
            <h1 className="courier-panel-title">Validacao obrigatoria do motoboy</h1>
            <p className="courier-panel-intro">
              Antes de acessar o painel, conclua as 2 etapas: selfie e documento com foto (RG/CNH).
            </p>

            {isVerificationRejected ? (
              <div className="courier-verification-alert courier-verification-alert--rejected">
                Cadastro reprovado pelo admin. Reenvie selfie e documento para nova analise.
              </div>
            ) : null}

            {isVerificationPending ? (
              <div className="courier-verification-alert courier-verification-alert--pending">
                Seu envio foi recebido e esta em analise pelo admin.
              </div>
            ) : null}

            <div className="courier-verification-steps">
              <div className="courier-verification-step">
                <p className="courier-verification-step-title">Etapa 1 de 2: reconhecimento facial (selfie)</p>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => openCameraDialog('face')}
                  disabled={uploadingFace}
                >
                  {uploadingFace ? 'Enviando selfie...' : (hasFaceVerification ? 'Refazer selfie' : 'Abrir camera frontal')}
                </button>
                <p className="courier-verification-step-hint">
                  Tire uma foto nitida do seu rosto.
                </p>
              </div>

              <div className="courier-verification-step">
                <p className="courier-verification-step-title">Etapa 2 de 2: documento pessoal (RG/CNH)</p>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => openCameraDialog('document')}
                  disabled={uploadingDocument || !hasFaceVerification}
                >
                  {uploadingDocument ? 'Enviando documento...' : (hasDocumentVerification ? 'Refazer documento' : 'Abrir camera traseira')}
                </button>
                <p className="courier-verification-step-hint">
                  O documento fica salvo somente para validacao do admin.
                </p>
              </div>
            </div>
          </section>

          {cameraDialogOpen ? (
            <div className="courier-camera-modal-overlay">
              <div className="courier-camera-modal">
                <div className="courier-camera-modal__header">
                  <h3>{cameraTargetType === 'face' ? 'Tire sua selfie' : 'Fotografe seu documento'}</h3>
                  <button type="button" className="btn-link" onClick={closeCameraDialog}>Fechar</button>
                </div>

                <div className="courier-camera-modal__preview">
                  <video ref={cameraVideoRef} autoPlay muted playsInline className="courier-camera-video" />
                  {cameraTargetType === 'face' ? <div className="courier-camera-face-guide" /> : null}
                </div>

                {cameraError ? (
                  <p className="courier-camera-modal__error">{cameraError}</p>
                ) : (
                  <p className="courier-camera-modal__hint">
                    {cameraTargetType === 'face'
                      ? 'Centralize seu rosto na moldura e toque em capturar.'
                      : 'Mantenha RG/CNH legivel e bem iluminado.'}
                  </p>
                )}

                <div className="courier-camera-modal__actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleCaptureFromCamera}
                    disabled={Boolean(cameraError) || uploadingFace || uploadingDocument}
                  >
                    {uploadingFace || uploadingDocument ? 'Enviando...' : 'Capturar e enviar'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (view === 'map') {
    return (
      <div className="courier-map-screen" style={{ position: 'fixed', inset: 0, background: '#0b0b0f', color: '#fff' }}>
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <TrackingMap
            key={`courier-map-${useNightMapStyle ? 'dark' : 'light'}`}
            courierLocation={courierCoords}
            restaurantLocation={displayRestaurantCoords}
            customerLocation={displayCustomerCoords}
            route={routeData?.geometry ? { type: 'Feature', geometry: routeData.geometry, properties: {} } : null}
            routes={combinedRoutes}
            extraMarkers={combinedMarkers}
            courierLabel={me?.name || user?.email}
            restaurantLabel={displayRestaurantLabel}
            customerLabel={displayCustomerLabel}
            customerKind={displayCustomerKind}
            mapStyleUrl={courierMapStyleUrl}
            height="100vh"
            fitPadding={80}
          />
        </div>

        <div className="courier-map-header">
          <button
            className="courier-map-control courier-map-control--button courier-map-control--account"
            onClick={handleBackToProfile}
          >
            <ArrowLeft size={15} />
            Meu cadastro
          </button>
          <button
            className="courier-map-control courier-map-control--button courier-map-control--offline"
            onClick={handleGoOffline}
            disabled={updatingOnline}
          >
            {updatingOnline ? 'Atualizando...' : 'Ficar offline'}
          </button>
          <div className="courier-map-control courier-map-control--stats courier-map-control--earnings">
            <span className="courier-map-earnings-card__label">Saldo da sessao</span>
            <strong className="courier-map-earnings-card__value">{formatMoney(pendingSessionCents)}</strong>
            <span className="courier-map-earnings-card__meta">Corridas concluidas: {deliveriesCompletedTotal}</span>
          </div>
          <div className="courier-map-control courier-map-control--status courier-map-control--availability">
            Status: {me?.is_online ? 'Online' : 'Offline'}
          </div>
          <button
            className="courier-map-control courier-map-control--button courier-map-control--location"
            onClick={handleUpdateLocation}
            disabled={updatingLocation || !me?.is_online}
          >
            {updatingLocation ? 'Atualizando...' : 'Atualizar localizacao'}
          </button>
          {hasMobileInfoContent ? (
            <button
              type="button"
              className="courier-map-control courier-map-control--button courier-mobile-info-toggle"
              onClick={() => setShowMobileInfoPanel((prev) => !prev)}
            >
              {showMobileInfoPanel ? 'Ocultar informacoes' : 'Ver informacoes'}
            </button>
          ) : null}
        </div>

        {activeOrderPhaseClock ? (
          <div className={`courier-map-phase-clock courier-map-surface ${activeOrderPhaseClock.isOverdue ? 'is-overdue' : ''}`}>
            <p className="courier-map-phase-clock__instance">{activeOrderPhaseClock.instanceLabel}</p>
            <div className="courier-map-phase-clock__row">
              <span>{activeOrderPhaseClock.clockLabel}</span>
              <strong>{activeOrderPhaseClock.countdown}</strong>
            </div>
          </div>
        ) : null}

        {showInProgressCard ? (
          <div className="courier-map-card courier-map-card--right courier-map-surface" style={{ position: 'absolute', right: 16, top: 140, width: 260, zIndex: 2, pointerEvents: 'auto' }}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Entregas em andamento</p>
            {deliveryOrders.map((order) => {
              const serviceBadge = getServiceBadgeLabel(order);
              const serviceHint = getServiceGearHint(order);
              return (
                <div key={order.id} style={{ fontSize: 13, display: 'grid', gap: 4, marginBottom: 10 }} onClick={() => setFocusedOrderId(order.id)}>
                  <p>Cliente: {order?.customer?.name || order?.customer_name || order?.delivery_address?.name || 'Cliente'}</p>
                  <p>Endereco: {formatAddress(order?.delivery_address)}</p>
                  <p>Pedido: {order?.order_number || order?.id}</p>
                  {serviceBadge ? <p><span className="courier-service-badge">{serviceBadge}</span> <span className="courier-service-hint-inline">{serviceHint}</span></p> : null}
                  <p>Status: {order?.status || '-'}</p>
                </div>
              );
            })}
          </div>
        ) : null}



        {availableOrders.length > 0 ? (
          <div className="courier-map-card courier-map-card--right courier-map-card--preview courier-map-surface" style={{ position: 'absolute', right: 16, top: showInProgressCard ? 340 : 140, width: 280, maxHeight: 320, overflow: 'auto', zIndex: 2, pointerEvents: 'auto' }}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Pedidos disponiveis</p>
            {availableOrders.map((order) => {
              const selected = order.id === selectedAvailableOrderId;
              const deliveryDistanceKm = resolveStoreCustomerDistanceKm(order);
              const serviceBadge = getServiceBadgeLabel(order);
              const serviceHint = getServiceGearHint(order);
              const serviceCardClass = getServiceCardClass(order);
              return (
                <div
                  key={order.id}
                  className={`courier-available-order-card ${selected ? 'is-selected' : ''} ${serviceCardClass}`}
                  onClick={() => setSelectedAvailableOrderId(order.id)}
                >
                  <div className="courier-available-order-head">
                    <p><strong>{order?.order_number || order?.id}</strong> {selected ? '? selecionado' : ''}</p>
                    {serviceBadge ? <span className="courier-service-badge">{serviceBadge}</span> : null}
                  </div>
                  <p>Restaurante: {order?.restaurant?.name || '-'}</p>
                  <p>Cliente: {order?.customer?.name || order?.customer_name || order?.delivery_address?.name || 'Cliente'}</p>
                  <p>Endereco: {formatAddress(order?.delivery_address)}</p>
                  {Number.isFinite(deliveryDistanceKm) && <p>Distancia loja-cliente: {formatDistanceKm(deliveryDistanceKm)}</p>}
                  <p>Valor da corrida: {formatMoney(resolveCourierRideNet(order))}</p>
                  {serviceHint ? <p className="courier-service-hint">{serviceHint}</p> : null}
                  <div className="courier-available-order-actions">
                    <button
                      className="btn-primary"
                      onClick={(event) => { event.stopPropagation(); handleAcceptOrder(order.id); }}
                      disabled={acceptingId || rejectingId || !canAcceptMoreOrders || loadingLists}
                      style={{ minWidth: 110 }}
                    >
                      {acceptingId === order.id ? 'Aceitando...' : 'Aceitar'}
                    </button>
                    <button
                      type="button"
                      className="courier-map-control courier-map-control--button courier-map-reject-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRejectOrder(order.id);
                      }}
                      disabled={acceptingId || rejectingId || loadingLists}
                    >
                      {rejectingId === order.id ? 'Rejeitando...' : 'Rejeitar'}
                    </button>
                  </div>
                </div>
              );
            })}
            {previewRouteLoading && <p style={{ marginTop: 6 }}>Carregando rotas...</p>}
            {previewRouteError && <p style={{ marginTop: 6, color: '#fca5a5' }}>{previewRouteError}</p>}
          </div>
        ) : null}

        {locationError ? (
          <div className="courier-map-status-alert courier-map-status-alert--error courier-map-surface" style={{ position: 'absolute', right: 16, top: 132, maxWidth: 260, zIndex: 2, pointerEvents: 'auto' }}>
            {locationError}
          </div>
        ) : null}

        {cancelledOrderNotice ? (
          <div
            className="courier-map-status-alert courier-map-status-alert--cancelled courier-map-surface"
            style={{ position: 'absolute', left: 16, top: canShowPickupWarning ? 180 : 132, maxWidth: 360, zIndex: 2, pointerEvents: 'auto' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <strong>Pedido cancelado</strong>
              <button
                type="button"
                className="courier-map-cancelled-close"
                onClick={() => setCancelledOrderNotice(null)}
                aria-label="Fechar aviso de pedido cancelado"
              >
                x
              </button>
            </div>
            <p style={{ margin: '6px 0 0' }}>
              O pedido <strong>{cancelledOrderNotice.orderLabel}</strong> foi cancelado.
            </p>
          </div>
        ) : null}

        {hasPendingCashReturns && !isMobileViewport ? (
          <div
            className={`courier-map-card courier-map-card--cash-return courier-map-surface ${cashReturnBlocked ? 'is-blocked' : ''}`}
            style={{ position: 'absolute', right: 16, bottom: showCodePanel ? 244 : 92, width: 320, zIndex: 2, pointerEvents: 'auto' }}
          >
            <p className="courier-map-cash-return-title">Devolucao de dinheiro</p>
            <p className="courier-map-cash-return-summary">
              {cashReturnPendingCount} pendencia(s) | Total: <strong>{formatMoney(cashReturnPendingTotalCents)}</strong>
            </p>
            {cashReturnBlocked ? (
              <p className="courier-map-cash-return-alert">
                Conta bloqueada por atraso acima de 24h. Regularize com codigo da loja.
              </p>
            ) : null}

            <div className="courier-map-cash-return-stores">
              {(cashReturnStores.slice(0, 3)).map((store) => (
                <div key={`${store.restaurant_id}-${store.restaurant_name}`} className="courier-map-cash-return-store">
                  <span>{store.restaurant_name || 'Loja'}</span>
                  <strong>{formatMoney(store.pending_total_cents || 0)}</strong>
                </div>
              ))}
            </div>

            {selectedCashReturnItem ? (
              <div className="courier-map-cash-return-form">
                <label className="courier-map-cash-return-label">
                  Pedido com pendencia
                  <select
                    value={String(selectedCashReturnId || '')}
                    onChange={(event) => {
                      setSelectedCashReturnId(event.target.value);
                      setCashReturnCode('');
                      if (cashReturnCodeError) setCashReturnCodeError('');
                    }}
                  >
                    {cashReturnItems.map((item) => {
                      const orderNumber = item?.order_number || '-';
                      const storeName = item?.restaurant?.name || item?.restaurant_name || 'Loja';
                      return (
                        <option key={item.id} value={item.id}>
                          {orderNumber} | {storeName}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <div className="courier-map-cash-return-meta">
                  <span>Status: {formatCashReturnStatusLabel(selectedCashReturnItem?.status)}</span>
                  <span>Vence em: {formatDate(selectedCashReturnItem?.due_at)}</span>
                </div>
                <div className="courier-map-cash-return-input-row">
                  <input
                    value={cashReturnCode}
                    onChange={(event) => {
                      setCashReturnCode(String(event.target.value || '').replace(/\D/g, '').slice(0, 8));
                      if (cashReturnCodeError) setCashReturnCodeError('');
                    }}
                    placeholder="Codigo informado pelo lojista"
                    inputMode="numeric"
                    maxLength={8}
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleConfirmCashReturnCode}
                    disabled={confirmingCashReturnId === String(selectedCashReturnItem?.id || '')}
                  >
                    {confirmingCashReturnId === String(selectedCashReturnItem?.id || '') ? 'Confirmando...' : 'Confirmar'}
                  </button>
                </div>
                {cashReturnCodeError ? (
                  <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{cashReturnCodeError}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {showPickupCard ? (
          <div className="courier-map-card courier-map-card--left courier-map-surface" style={{ position: 'absolute', left: 16, bottom: showCodePanel ? 220 : 88, maxWidth: 340, zIndex: 2, pointerEvents: 'auto' }}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Pedidos aguardando retirada</p>
            {pickupOrders.map((order) => {
              const serviceBadge = getServiceBadgeLabel(order);
              const serviceHint = getServiceGearHint(order);
              return (
                <div key={order.id} style={{ marginBottom: 8, fontSize: 13 }} onClick={() => setFocusedOrderId(order.id)}>
                  <p><strong>{order.order_number || order.id}</strong></p>
                  {serviceBadge ? <p><span className="courier-service-badge">{serviceBadge}</span> <span className="courier-service-hint-inline">{serviceHint}</span></p> : null}
                  <p>Status: {order.status || '-'}</p>
                  <p>Restaurante: {order?.restaurant?.name || '-'}</p>
                </div>
              );
            })}
            {activeOrder && isAwaitingPickup ? (
              <>
                {routeLoading && <p>Carregando rota...</p>}
                {routeError && <p style={{ color: '#fca5a5' }}>{routeError}</p>}
                {routeData?.distance_m != null && routeData?.duration_s != null && (
                  <p>
                    Distancia: {(routeData.distance_m / 1000).toFixed(1).replace('.', ',')} km | Tempo: {Math.round(routeData.duration_s / 60)} min
                  </p>
                )}
              </>
            ) : null}
          </div>
        ) : null}

        {canShowPickupWarning ? (
          <div className="courier-map-status-alert courier-map-status-alert--warning courier-map-surface" style={{ position: 'absolute', left: 16, top: 132, zIndex: 2, pointerEvents: 'auto' }}>
            Alguns pedidos ainda sem coordenadas de retirada. O filtro de raio pode falhar.
          </div>
        ) : null}


        {hasMobileInfoContent ? (
          <div
            className={`courier-mobile-info-sheet ${showMobileInfoPanel ? 'open' : ''}`}
            style={{ bottom: showCodePanel ? 230 : 88 }}
          >
            <div className="courier-mobile-info-sheet__header">
              <strong>Informacoes dos pedidos</strong>
              <button type="button" className="courier-mobile-info-sheet__close" onClick={() => setShowMobileInfoPanel(false)}>
                Fechar
              </button>
            </div>

            {locationError ? (
              <div className="courier-mobile-info-sheet__alert courier-mobile-info-sheet__alert--error">{locationError}</div>
            ) : null}

            {cancelledOrderNotice ? (
              <div className="courier-mobile-info-sheet__alert courier-mobile-info-sheet__alert--cancelled">
                Pedido <strong>{cancelledOrderNotice.orderLabel}</strong> cancelado.
              </div>
            ) : null}

            {canShowPickupWarning ? (
              <div className="courier-mobile-info-sheet__alert courier-mobile-info-sheet__alert--warning">
                Alguns pedidos ainda sem coordenadas de retirada. O filtro de raio pode falhar.
              </div>
            ) : null}

            {showPickupCard ? (
              <div className="courier-mobile-info-sheet__section">
                <p className="courier-mobile-info-sheet__title">Pedidos aguardando retirada</p>
                {pickupOrders.map((order) => (
                  <button
                    key={order.id}
                    type="button"
                    className="courier-mobile-info-sheet__item"
                    onClick={() => setFocusedOrderId(order.id)}
                  >
                    <p><strong>{order.order_number || order.id}</strong></p>
                    {getServiceBadgeLabel(order) ? (
                      <p><strong>{getServiceBadgeLabel(order)}</strong> {getServiceGearHint(order)}</p>
                    ) : null}
                    <p>Status: {order.status || '-'}</p>
                    <p>Restaurante: {order?.restaurant?.name || '-'}</p>
                  </button>
                ))}
              </div>
            ) : null}

            {showInProgressCard ? (
              <div className="courier-mobile-info-sheet__section">
                <p className="courier-mobile-info-sheet__title">Entregas em andamento</p>
                {deliveryOrders.map((order) => (
                  <button
                    key={order.id}
                    type="button"
                    className="courier-mobile-info-sheet__item"
                    onClick={() => setFocusedOrderId(order.id)}
                  >
                    <p>Cliente: {order?.customer?.name || order?.customer_name || order?.delivery_address?.name || 'Cliente'}</p>
                    <p>Endereco: {formatAddress(order?.delivery_address)}</p>
                    <p>Pedido: {order?.order_number || order?.id}</p>
                    {getServiceBadgeLabel(order) ? (
                      <p><strong>{getServiceBadgeLabel(order)}</strong> {getServiceGearHint(order)}</p>
                    ) : null}
                    <p>Status: {order?.status || '-'}</p>
                  </button>
                ))}
              </div>
            ) : null}

            {availableOrders.length > 0 ? (
              <div className="courier-mobile-info-sheet__section">
                <p className="courier-mobile-info-sheet__title">Pedidos disponiveis</p>
                {availableOrders.map((order) => {
                  const selected = order.id === selectedAvailableOrderId;
                  const deliveryDistanceKm = resolveStoreCustomerDistanceKm(order);
                  const serviceBadge = getServiceBadgeLabel(order);
                  const serviceHint = getServiceGearHint(order);
                  const serviceCardClass = getServiceCardClass(order);
                  return (
                    <div key={order.id} className={`courier-mobile-info-sheet__item ${selected ? 'is-selected' : ''} ${serviceCardClass}`}>
                      <button type="button" className="courier-mobile-info-sheet__item-button" onClick={() => setSelectedAvailableOrderId(order.id)}>
                        <p><strong>{order?.order_number || order?.id}</strong> {selected ? '? selecionado' : ''}</p>
                        {serviceBadge ? <p><strong>{serviceBadge}</strong></p> : null}
                        <p>Restaurante: {order?.restaurant?.name || '-'}</p>
                        <p>Cliente: {order?.customer?.name || order?.customer_name || order?.delivery_address?.name || 'Cliente'}</p>
                        <p>Endereco: {formatAddress(order?.delivery_address)}</p>
                        {Number.isFinite(deliveryDistanceKm) && <p>Distancia loja-cliente: {formatDistanceKm(deliveryDistanceKm)}</p>}
                        <p>Valor da corrida: {formatMoney(resolveCourierRideNet(order))}</p>
                        {serviceHint ? <p className="courier-service-hint">{serviceHint}</p> : null}
                      </button>
                      <div className="courier-mobile-info-sheet__actions">
                        <button
                          className="btn-primary courier-mobile-info-sheet__accept"
                          onClick={(event) => { event.stopPropagation(); handleAcceptOrder(order.id); }}
                          disabled={acceptingId || rejectingId || !canAcceptMoreOrders || loadingLists}
                        >
                          {acceptingId === order.id ? 'Aceitando...' : 'Aceitar'}
                        </button>
                        <button
                          type="button"
                          className="courier-map-control courier-map-control--button courier-map-reject-btn courier-mobile-info-sheet__reject"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRejectOrder(order.id);
                          }}
                          disabled={acceptingId || rejectingId || loadingLists}
                        >
                          {rejectingId === order.id ? 'Rejeitando...' : 'Rejeitar'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {hasPendingCashReturns ? (
              <div className="courier-mobile-info-sheet__section">
                <p className="courier-mobile-info-sheet__title">Devolucao de dinheiro</p>
                <p className="courier-mobile-info-sheet__small">
                  {cashReturnPendingCount} pendencia(s) | Total: {formatMoney(cashReturnPendingTotalCents)}
                </p>
                {cashReturnBlocked ? (
                  <div className="courier-mobile-info-sheet__alert courier-mobile-info-sheet__alert--error">
                    Conta bloqueada por devolucao em atraso. Regularize com codigo da loja.
                  </div>
                ) : null}
                {selectedCashReturnItem ? (
                  <div className="courier-mobile-cash-return-card">
                    <label>
                      Pedido com pendencia
                      <select
                        value={String(selectedCashReturnId || '')}
                        onChange={(event) => {
                          setSelectedCashReturnId(event.target.value);
                          setCashReturnCode('');
                          if (cashReturnCodeError) setCashReturnCodeError('');
                        }}
                      >
                        {cashReturnItems.map((item) => {
                          const orderNumber = item?.order_number || '-';
                          const storeName = item?.restaurant?.name || item?.restaurant_name || 'Loja';
                          return (
                            <option key={item.id} value={item.id}>
                              {orderNumber} | {storeName}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                    <p className="courier-mobile-info-sheet__small">
                      Status: {formatCashReturnStatusLabel(selectedCashReturnItem?.status)} | Vence em: {formatDate(selectedCashReturnItem?.due_at)}
                    </p>
                    <div className="courier-mobile-cash-return-actions">
                      <input
                        value={cashReturnCode}
                        onChange={(event) => {
                          setCashReturnCode(String(event.target.value || '').replace(/\D/g, '').slice(0, 8));
                          if (cashReturnCodeError) setCashReturnCodeError('');
                        }}
                        placeholder="Codigo do lojista"
                        inputMode="numeric"
                        maxLength={8}
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleConfirmCashReturnCode}
                        disabled={confirmingCashReturnId === String(selectedCashReturnItem?.id || '')}
                      >
                        {confirmingCashReturnId === String(selectedCashReturnItem?.id || '') ? 'Confirmando...' : 'Confirmar devolucao'}
                      </button>
                    </div>
                    {cashReturnCodeError ? (
                      <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{cashReturnCodeError}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="courier-map-actions" style={{ position: 'absolute', left: '50%', bottom: showCodePanel ? 160 : 24, transform: 'translateX(-50%)', display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', zIndex: 2, pointerEvents: 'auto' }}>
          <button
            className="courier-map-control courier-map-control--button courier-map-action-btn"
            onClick={() => nextAvailableOrder && handleAcceptOrder(nextAvailableOrder.id)}
            disabled={!nextAvailableOrder || acceptingId || rejectingId || !canAcceptMoreOrders || loadingLists}
            style={{ minWidth: 148 }}
          >
            {nextAvailableOrder
              ? acceptingId ? 'Aceitando...' : (hasActiveOrder ? 'Aceitar outra corrida' : 'Aceitar corrida')
              : (hasActiveOrder ? 'Sem novas corridas' : 'Sem corridas')}
          </button>

          <button
            className="courier-map-control courier-map-control--button courier-map-secondary-btn"
            onClick={handleToggleCodePanel}
            disabled={!activeOrder}
            style={{ minWidth: 158 }}
          >
            Inserir codigo do cliente
          </button>
        </div>

        {showCodePanel ? (
          <div className="courier-code-panel" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, background: '#0b0b0f', borderTop: '1px solid #1f2937', padding: 20, zIndex: 3, pointerEvents: 'auto' }}>
            <h3 style={{ marginBottom: 12 }}>Confirmar entrega</h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {canConfirmDelivery ? (
              <p style={{ width: '100%', marginTop: 8, color: '#93c5fd', fontSize: 12 }}>
                Solicite ao cliente o codigo de confirmacao do pedido.
              </p>
            ) : (
              <p style={{ width: '100%', marginTop: 8, color: '#fca5a5', fontSize: 12 }}>
                A confirmacao so e permitida quando o pedido estiver em rota de entrega.
              </p>
            )}
              <input
                ref={codeInputRef}
                value={deliveryCode}
                onChange={(e) => setDeliveryCode(String(e.target.value || '').replace(/\D/g, '').slice(0, 8))}
                placeholder="Codigo de confirmacao informado pelo cliente"
                inputMode="numeric"
                maxLength={8}
                style={{ flex: 1, minWidth: 220, padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#111827', color: '#fff' }}
              />
              <button
                className="btn-primary"
                onClick={handleConfirmDeliveryCode}
                disabled={submittingCode || !canConfirmDelivery || updatingOrderId === activeOrder?.id}
                style={{ minWidth: 180 }}
              >
                {submittingCode ? 'Confirmando...' : 'Confirmar entrega'}
              </button>
              <button
                className="btn-link"
                onClick={() => setShowCodePanel(false)}
                style={{ color: '#fff' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="courier-page-shell">
      <div className="courier-panel-page">
      <button className="btn-link courier-panel-back" onClick={handleLogout}>
        <LogOut size={16} />
        Sair
      </button>

      <h1 className="courier-panel-title">Painel do Motoboy</h1>

      {isMobileViewport ? (
        <nav className="courier-mobile-quick-nav" aria-label="Atalhos do painel do motoboy">
          <button
            type="button"
            className={`courier-mobile-quick-nav__item ${isProfileSectionOpen ? 'is-active' : ''}`}
            onClick={() => scrollToMobilePanelSection('profile', profileSectionRef)}
          >
            Cadastro
          </button>
          <button
            type="button"
            className={`courier-mobile-quick-nav__item ${isAvailabilitySectionOpen ? 'is-active' : ''}`}
            onClick={() => scrollToMobilePanelSection('availability', availabilitySectionRef)}
          >
            Disponibilidade
          </button>
          <button
            type="button"
            className={`courier-mobile-quick-nav__item ${isPayoutSectionOpen ? 'is-active' : ''}`}
            onClick={() => scrollToMobilePanelSection('payout', payoutSectionRef)}
          >
            Recebimento
          </button>
        </nav>
      ) : null}

      <section
        ref={profileSectionRef}
        className={`card courier-panel-section ${isProfileSectionOpen ? 'is-mobile-open' : 'is-mobile-collapsed'}`}
      >
        <div className="courier-mobile-section-head">
          <h2 className="courier-panel-section-title">Meu cadastro</h2>
          {isMobileViewport ? (
            <button
              type="button"
              className="courier-mobile-section-toggle"
              onClick={() => toggleMobilePanelSection('profile')}
              aria-expanded={isProfileSectionOpen}
            >
              {isProfileSectionOpen ? 'Ocultar' : 'Expandir'}
            </button>
          ) : null}
        </div>
        {isProfileSectionOpen ? (
          loadingProfile ? (
          <p>Carregando cadastro...</p>
        ) : (
          <form className="courier-panel-form courier-profile-form" onSubmit={handleSaveProfile}>
            <div className="courier-profile-grid">
              <div className="courier-profile-fields">
                <label className="courier-profile-field">
                  <span>Nome</span>
                  <input className="courier-profile-input" value={profileForm.name} readOnly />
                </label>

                <label className="courier-profile-field">
                  <span>E-mail</span>
                  <input className="courier-profile-input" value={emailValue} readOnly />
                </label>

                <label className="courier-profile-field">
                  <span>CPF</span>
                  <input className="courier-profile-input" value={profileForm.cpf} readOnly />
                </label>

                <label className="courier-profile-field">
                  <span>Telefone</span>
                  <input
                    className="courier-profile-input"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))}
                  />
                </label>

                <label className="courier-profile-field">
                  <span>Placa</span>
                  <input
                    className="courier-profile-input"
                    value={profileForm.placa}
                    onChange={(e) => setProfileForm((p) => ({ ...p, placa: e.target.value }))}
                  />
                </label>

                <div className="courier-profile-required-grid">
                  <label className="courier-profile-field">
                    <span>Marca da moto *</span>
                    <input
                      className="courier-profile-input"
                      value={profileForm.motorcycle_brand}
                      onChange={(e) => setProfileForm((p) => ({ ...p, motorcycle_brand: e.target.value }))}
                    />
                  </label>

                  <label className="courier-profile-field">
                    <span>Modelo da moto *</span>
                    <input
                      className="courier-profile-input"
                      value={profileForm.motorcycle_model}
                      onChange={(e) => setProfileForm((p) => ({ ...p, motorcycle_model: e.target.value }))}
                    />
                  </label>

                  <label className="courier-profile-field">
                    <span>Contato de emergencia *</span>
                    <input
                      className="courier-profile-input"
                      value={profileForm.emergency_contact_phone}
                      onChange={(e) => setProfileForm((p) => ({ ...p, emergency_contact_phone: e.target.value }))}
                    />
                  </label>

                  <label className="courier-profile-field">
                    <span>Data de nascimento *</span>
                    <input
                      className="courier-profile-input"
                      type="date"
                      value={normalizeBirthDateValue(profileForm.birth_date)}
                      onChange={(e) => setProfileForm((p) => ({ ...p, birth_date: normalizeBirthDateValue(e.target.value) }))}
                    />
                  </label>
                </div>
              </div>

              <div className="courier-profile-face-card courier-profile-photo-card">
                <div className="courier-profile-photo-frame">
                  {effectiveVerification?.face_image_url ? (
                    <img
                      src={effectiveVerification.face_image_url}
                      alt="Foto do motoboy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div className="courier-profile-photo-empty">
                      Sem foto do rosto
                    </div>
                  )}
                </div>
                <p className="courier-profile-photo-caption">Foto do motoboy</p>
              </div>
            </div>
            {isVerificationRejected ? (
              <div className="courier-verification-retry-card">
                <div className="courier-verification-retry-header">
                  <strong>Validacao de identidade</strong>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      color: verificationPresentation.color,
                      background: verificationPresentation.background,
                    }}
                  >
                    {verificationPresentation.label}
                  </span>
                </div>
                <p className="courier-verification-retry-text">
                  Cadastro reprovado pelo ADM. Reenvie selfie e documento para fazer novamente o processo de validacao.
                </p>
                <div className="courier-verification-retry-grid">
                  <div className="courier-verification-retry-col">
                    <span>Selfie (rosto)</span>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => openCameraDialog('face')}
                      disabled={uploadingFace}
                    >
                      {uploadingFace ? 'Enviando selfie...' : 'Abrir camera frontal'}
                    </button>
                    <small className="courier-verification-retry-hint">Selfie enviada: {hasFaceVerification ? 'Sim' : 'Nao'}</small>
                  </div>

                  <div className="courier-verification-retry-col">
                    <span>Documento com foto</span>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => openCameraDialog('document')}
                      disabled={uploadingDocument}
                    >
                      {uploadingDocument ? 'Enviando documento...' : 'Abrir camera traseira'}
                    </button>
                    <small className="courier-verification-retry-hint">
                      Documento enviado: {hasDocumentVerification ? 'Sim' : 'Nao'} (imagem nao exibida no app)
                    </small>
                  </div>
                </div>
              </div>
            ) : null}
            <p className="courier-profile-immutable-note">
              Nome e CPF nao podem ser alterados apos o cadastro.
            </p>
            <button type="submit" className="btn-primary" disabled={savingProfile}>
              {savingProfile ? 'Salvando...' : 'Salvar cadastro'}
            </button>
          </form>
        )
        ) : (
          <p className="courier-mobile-section-summary">
            Telefone: {profileForm.phone || '-'} | Placa: {profileForm.placa || '-'} | Verificacao: {verificationPresentation.label}
          </p>
        )}
      </section>

      <section
        ref={availabilitySectionRef}
        className={`card courier-panel-section ${isAvailabilitySectionOpen ? 'is-mobile-open' : 'is-mobile-collapsed'}`}
      >
        <div className="courier-mobile-section-head">
          <h2 className="courier-panel-section-title">Disponibilidade</h2>
          {isMobileViewport ? (
            <button
              type="button"
              className="courier-mobile-section-toggle"
              onClick={() => toggleMobilePanelSection('availability')}
              aria-expanded={isAvailabilitySectionOpen}
            >
              {isAvailabilitySectionOpen ? 'Ocultar' : 'Expandir'}
            </button>
          ) : null}
        </div>
        {isAvailabilitySectionOpen ? (
          <>
            <div className="courier-panel-row">
              <button className="btn-primary" onClick={handleGoOnline} disabled={updatingOnline || !canRequestOnline}>
                {updatingOnline ? 'Atualizando...' : availabilityLabel}
              </button>
              <span>Status: {me?.is_online ? 'Online' : 'Offline'}</span>
              <span>Ultima disponibilidade: {formatDate(me?.last_available_at)}</span>
            </div>
            {!canRequestOnline || me?.is_online ? (
              <div className="courier-availability-warning">
                {missingValidationDataFields.length > 0 ? (
                  <p className="courier-availability-warning-text">
                    Faltam dados obrigatorios para validacao: {missingValidationDataFields.join(', ')}.
                  </p>
                ) : me?.is_online && effectiveVerification?.status !== 'approved' ? (
                  <p className="courier-availability-warning-text">
                    Seu cadastro esta em analise. Regularize para evitar bloqueio operacional.
                  </p>
                ) : (
                  <p className="courier-availability-warning-text">
                    Para ficar online, seu cadastro deve estar aprovado no painel admin.
                  </p>
                )}
              </div>
            ) : null}
          </>
        ) : (
          <p className="courier-mobile-section-summary">
            Status atual: {me?.is_online ? 'Online' : 'Offline'} | Ultima atualizacao: {formatDate(me?.last_available_at)}
          </p>
        )}
      </section>
      <section
        ref={payoutSectionRef}
        className={`card courier-panel-section ${isPayoutSectionOpen ? 'is-mobile-open' : 'is-mobile-collapsed'}`}
      >
        <div className="courier-mobile-section-head">
          <h2 className="courier-panel-section-title">Recebimento</h2>
          {isMobileViewport ? (
            <button
              type="button"
              className="courier-mobile-section-toggle"
              onClick={() => toggleMobilePanelSection('payout')}
              aria-expanded={isPayoutSectionOpen}
            >
              {isPayoutSectionOpen ? 'Ocultar' : 'Expandir'}
            </button>
          ) : null}
        </div>
        {isPayoutSectionOpen ? (
          <div className="courier-payout-grid">
          <div className="courier-payout-summary">
            <div className="courier-payout-stat">
              <span>Total acumulado</span>
              <strong>{formatMoney(pendingTotalCents)}</strong>
              <small>Valor total das corridas aguardando pagamento.</small>
            </div>
            <div className="courier-payout-stat">
              <span>Saldo da sessao (mapa)</span>
              <strong>{formatMoney(pendingSessionCents)}</strong>
              <small>Este valor zera apos ficar offline por 3 a 4 horas.</small>
            </div>
            <div className="courier-payout-stat">
              <span>Valor consolidado no perfil</span>
              <strong>{formatMoney(readyForPayoutCents)}</strong>
              <small>Pronto para o ADM programar seu pagamento agora.</small>
            </div>
            <div className="courier-payout-stat">
              <span>Aguardando proximo ciclo</span>
              <strong>{formatMoney(waitingNextCycleCents)}</strong>
              <small>Valores acumulados para o proximo pagamento.</small>
            </div>
            <div className="courier-payout-stat">
              <span>Corridas concluidas</span>
              <strong>{deliveriesCompletedTotal}</strong>
              <small>Total de entregas finalizadas neste cadastro.</small>
            </div>
            <div className="courier-payout-stat">
              <span>Acumulado do ciclo atual</span>
              <strong>{formatMoney(currentCycleUnpaidCents)}</strong>
              <small>Ciclo de referencia para fechamento ({payoutSchedule === 'monthly' ? 'mensal' : 'semanal'}).</small>
            </div>
            <div className="courier-payout-stat">
              <span>Total historico recebido</span>
              <strong>{formatMoney(lifetimeEarnedCents)}</strong>
              <small>Historico total acumulado de corridas.</small>
            </div>
            <button
              type="button"
              className="courier-payout-extra-toggle"
              onClick={() => setShowAdditionalInfo((prev) => !prev)}
              aria-expanded={showAdditionalInfo}
            >
              Informacoes adicionais {showAdditionalInfo ? '^' : 'v'}
            </button>
            {showAdditionalInfo ? (
              <div className="courier-payout-extra-content">
                <div className="courier-payout-note">
                  Regra de pagamento: {payoutSchedule === 'monthly'
                    ? 'mensal (5o dia util, ate 09:00).'
                    : 'semanal (ciclo de segunda 06:00 ate domingo 23:59; pagamento na segunda ate 09:00).'}{' '}
                  Proximo repasse previsto: {nextPayoutAt}.
                </div>
                <div className="courier-payout-note">
                  Troca de periodicidade: ao selecionar Mensal enquanto o recebimento esta Semanal, a solicitacao fica em espera ate o pagamento semanal de segunda-feira (ate 09:00). Ao selecionar Semanal enquanto o recebimento esta Mensal, a solicitacao fica em espera ate o pagamento mensal do 5o dia util (ate 09:00).
                </div>
                <div className="courier-payout-note">
                  Apos o pagamento do ciclo atual, a nova periodicidade e ativada automaticamente.
                </div>
                <div className="courier-payout-note">
                  Taxa ADM do Correfood: {courierAdminFeeInfoText}
                </div>
                {payoutSchedulePending ? (
                  <div className="courier-payout-note">
                    Solicitacao atual em espera: {payoutScheduleRequested === 'monthly' ? 'Mensal' : 'Semanal'}.
                  </div>
                ) : null}
                {hasRolloverCountdown ? (
                  <div className="courier-payout-note">
                    Sessao sera consolidada no perfil em aproximadamente {rolloverCountdownLabel}.
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className={`courier-cash-return-card ${cashReturnBlocked ? 'is-overdue' : ''}`}>
            <div className="courier-cash-return-card__header">
              <h3>Devolucao de dinheiro para loja</h3>
              <span>{cashReturnPendingCount} pendencia(s)</span>
            </div>
            <p className="courier-cash-return-card__summary">
              Total pendente de devolucao: <strong>{formatMoney(cashReturnPendingTotalCents)}</strong>
            </p>
            {cashReturnBlocked ? (
              <p className="courier-cash-return-card__alert">
                Bloqueio ativo por pendencia vencida. Confirme a devolucao para desbloquear.
              </p>
            ) : null}

            {hasPendingCashReturns ? (
              <>
                <div className="courier-cash-return-stores">
                  {cashReturnStores.map((store) => (
                    <div key={`${store.restaurant_id}-${store.restaurant_name}`} className="courier-cash-return-store-row">
                      <div>
                        <strong>{store.restaurant_name || 'Loja'}</strong>
                        <small>{store.pending_count || 0} pedido(s)</small>
                      </div>
                      <span>{formatMoney(store.pending_total_cents || 0)}</span>
                    </div>
                  ))}
                </div>

                {selectedCashReturnItem ? (
                  <div className="courier-cash-return-confirm">
                    <label>
                      Pedido para confirmar devolucao
                      <select
                        value={String(selectedCashReturnId || '')}
                        onChange={(event) => {
                          setSelectedCashReturnId(event.target.value);
                          setCashReturnCode('');
                          if (cashReturnCodeError) setCashReturnCodeError('');
                        }}
                      >
                        {cashReturnItems.map((item) => {
                          const orderNumber = item?.order_number || '-';
                          const storeName = item?.restaurant?.name || item?.restaurant_name || 'Loja';
                          return (
                            <option key={item.id} value={item.id}>
                              {orderNumber} | {storeName}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                    <div className="courier-cash-return-confirm__meta">
                      <span>Status: {formatCashReturnStatusLabel(selectedCashReturnItem?.status)}</span>
                      <span>Vence em: {formatDate(selectedCashReturnItem?.due_at)}</span>
                      <span>Valor: {formatMoney(selectedCashReturnItem?.amount_total_cents || 0)}</span>
                    </div>
                    <div className="courier-cash-return-confirm__actions">
                      <input
                        value={cashReturnCode}
                        onChange={(event) => {
                          setCashReturnCode(String(event.target.value || '').replace(/\D/g, '').slice(0, 8));
                          if (cashReturnCodeError) setCashReturnCodeError('');
                        }}
                        placeholder="Digite o codigo informado pela loja"
                        inputMode="numeric"
                        maxLength={8}
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleConfirmCashReturnCode}
                        disabled={confirmingCashReturnId === String(selectedCashReturnItem?.id || '')}
                      >
                        {confirmingCashReturnId === String(selectedCashReturnItem?.id || '') ? 'Confirmando...' : 'Confirmar devolucao'}
                      </button>
                    </div>
                    {cashReturnCodeError ? (
                      <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{cashReturnCodeError}</p>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              <p className="courier-cash-return-card__empty">
                Nenhuma pendencia de devolucao em dinheiro no momento.
              </p>
            )}
          </div>

          <form className="courier-payout-form" onSubmit={handleSavePayout}>
            <h3>Dados para pagamento</h3>
            <p>Preencha PIX ou conta bancaria para o ADM realizar o repasse.</p>

            <label>
              Metodo de recebimento
              <select
                value={payoutMethod}
                onChange={(event) => setProfileForm((prev) => ({ ...prev, payout_method: event.target.value }))}
                disabled={payoutFieldsLocked || savingPayout}
              >
                <option value="pix">PIX</option>
                <option value="bank">Conta bancaria</option>
              </select>
            </label>

            {payoutMethod === 'pix' ? (
              <label>
                Chave PIX
                <input
                  value={profileForm.pix_key}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, pix_key: event.target.value }))}
                  placeholder="CPF, e-mail, celular ou chave aleatoria"
                  disabled={payoutFieldsLocked || savingPayout}
                />
              </label>
            ) : (
              <>
                <label>
                  Nome do banco
                  <input
                    value={profileForm.bank_name}
                    onChange={(event) => setProfileForm((prev) => ({ ...prev, bank_name: event.target.value }))}
                    placeholder="Ex.: Banco do Brasil"
                    disabled={payoutFieldsLocked || savingPayout}
                  />
                </label>
                <div className="courier-payout-form-inline">
                  <label>
                    Agencia
                    <input
                      value={profileForm.bank_agency}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, bank_agency: event.target.value }))}
                      placeholder="0001"
                      disabled={payoutFieldsLocked || savingPayout}
                    />
                  </label>
                  <label>
                    Conta
                    <input
                      value={profileForm.bank_account}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, bank_account: event.target.value }))}
                      placeholder="12345-6"
                      disabled={payoutFieldsLocked || savingPayout}
                    />
                  </label>
                </div>
                <div className="courier-payout-form-inline">
                  <label>
                    Tipo de conta
                    <input
                      value={profileForm.bank_account_type}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, bank_account_type: event.target.value }))}
                      placeholder="Corrente ou poupanca"
                      disabled={payoutFieldsLocked || savingPayout}
                    />
                  </label>
                  <label>
                    Documento do titular
                    <input
                      value={profileForm.bank_holder_document}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, bank_holder_document: event.target.value }))}
                      placeholder="CPF/CNPJ"
                      disabled={payoutFieldsLocked || savingPayout}
                    />
                  </label>
                </div>
                <label>
                  Nome do titular
                  <input
                    value={profileForm.bank_holder_name}
                    onChange={(event) => setProfileForm((prev) => ({ ...prev, bank_holder_name: event.target.value }))}
                    placeholder="Nome completo"
                    disabled={payoutFieldsLocked || savingPayout}
                  />
                </label>
              </>
            )}

            <div className="courier-payout-actions">
              <button
                type="submit"
                className={`btn-primary ${payoutFieldsLocked ? 'courier-payout-save-btn--saved' : ''}`}
                disabled={savingPayout || payoutFieldsLocked}
              >
                {savingPayout ? 'Salvando...' : payoutFieldsLocked ? 'Salvo' : 'Salvar conta bancaria'}
              </button>
              <button
                type="button"
                className="courier-payout-change-btn"
                onClick={() => setIsPayoutEditing(true)}
                disabled={savingPayout || !hasSavedPayoutData || isPayoutEditing}
              >
                Trocar dados de recebimento
              </button>
            </div>
            {payoutFieldsLocked ? (
              <small className="courier-payout-lock-hint">
                Dados de recebimento salvos. Para alterar, clique em "Trocar dados de recebimento".
              </small>
            ) : null}

            <div className="courier-payout-schedule-selector">
              <span className="courier-payout-schedule-selector__label">Periodicidade do recebimento</span>
              <div className="courier-payout-schedule-selector__buttons">
                <button
                  type="button"
                  className={`courier-payout-schedule-btn ${payoutSchedule === 'weekly' ? 'is-active' : ''} ${pendingTargetSchedule === 'weekly' ? 'is-pending is-requested' : ''}`}
                  onClick={() => handleSelectPayoutSchedule('weekly')}
                  disabled={savingPayoutSchedule}
                >
                  {pendingTargetSchedule === 'weekly' ? 'Semanal (em espera)' : 'Semanal'}
                </button>
                <button
                  type="button"
                  className={`courier-payout-schedule-btn ${payoutSchedule === 'monthly' ? 'is-active' : ''} ${pendingTargetSchedule === 'monthly' ? 'is-pending is-requested' : ''}`}
                  onClick={() => handleSelectPayoutSchedule('monthly')}
                  disabled={savingPayoutSchedule}
                >
                  {pendingTargetSchedule === 'monthly' ? 'Mensal (em espera)' : 'Mensal'}
                </button>
              </div>
              <small className="courier-payout-schedule-selector__hint">
                {payoutScheduleHint}
              </small>
              {payoutSchedulePending ? (
                <small className="courier-payout-schedule-selector__pending-message">
                  A alteracao para recebimento {pendingScheduleLabel} passara a valer depois do ultimo pagamento pendente.
                </small>
              ) : null}
            </div>
          </form>
        </div>
        ) : (
          <p className="courier-mobile-section-summary">
            Saldo pronto: {formatMoney(readyForPayoutCents)} | Proximo repasse: {nextPayoutAt}
          </p>
        )}
      </section>

      {cameraDialogOpen ? (
        <div className="courier-camera-modal-overlay">
          <div className="courier-camera-modal">
            <div className="courier-camera-modal__header">
              <h3>{cameraTargetType === 'face' ? 'Tire sua selfie' : 'Fotografe seu documento'}</h3>
              <button type="button" className="btn-link" onClick={closeCameraDialog}>Fechar</button>
            </div>

            <div className="courier-camera-modal__preview">
              <video ref={cameraVideoRef} autoPlay muted playsInline className="courier-camera-video" />
              {cameraTargetType === 'face' ? <div className="courier-camera-face-guide" /> : null}
            </div>

            {cameraError ? (
              <p className="courier-camera-modal__error">{cameraError}</p>
            ) : (
              <p className="courier-camera-modal__hint">
                {cameraTargetType === 'face'
                  ? 'Centralize seu rosto na moldura e toque em capturar.'
                  : 'Mantenha RG/CNH legivel e bem iluminado.'}
              </p>
            )}

            <div className="courier-camera-modal__actions">
              <button
                type="button"
                className="btn-primary"
                onClick={handleCaptureFromCamera}
                disabled={Boolean(cameraError) || uploadingFace || uploadingDocument}
              >
                {uploadingFace || uploadingDocument ? 'Enviando...' : 'Capturar e enviar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
}
